<template>
  <section class="knowledge-workspace" @keydown="handleKeydown" @click="closeFolderMenu">
    <header class="workspace-toolbar">
      <div class="workspace-brand"><span class="brand-mark">◆</span><strong>知识库</strong></div>
      <div class="workspace-search">
        <input v-model="searchText" placeholder="搜索文章、标签或路径…" @input="debouncedSearch" @keydown.enter="runSearch" />
        <span class="search-hint">Ctrl/Cmd + P</span>
      </div>
      <div class="workspace-actions">
        <button type="button" class="rail-toggle" :class="{ active: !vaultCollapsed }" :title="vaultCollapsed ? '展开文件栏' : '收起文件栏'" :aria-label="vaultCollapsed ? '展开文件栏' : '收起文件栏'" @click="toggleVaultSidebar">◧</button>
        <button type="button" class="rail-toggle" :class="{ active: !contextCollapsed }" :title="contextCollapsed ? '展开信息栏' : '收起信息栏'" :aria-label="contextCollapsed ? '展开信息栏' : '收起信息栏'" @click="toggleContextSidebar">◨</button>
        <button type="button" @click="newDocument">新建</button>
        <input ref="documentImportInput" class="document-import-input" type="file" multiple @change="handleDocumentImportPicker">
        <button type="button" class="document-import-action" :disabled="importingDocuments" @click="openDocumentImport">
          {{ documentImportLabel }}
        </button>
        <button type="button" class="download-action" :disabled="downloadingArchive" @click="downloadArchive">{{ downloadingArchive ? '打包中…' : '打包下载' }}</button>
        <button type="button" @click="toggleCommandPalette">命令</button>
        <button type="button" @click="$emit('close')">返回</button>
      </div>
    </header>

    <div class="workspace-body" :class="{ 'vault-collapsed': vaultCollapsed, 'context-collapsed': contextCollapsed }">
      <aside class="vault-sidebar" :class="{ 'mobile-open': mobileSidebar, 'is-collapsed': vaultCollapsed }">
        <div class="sidebar-toolbar">
          <span>Vault</span>
          <div class="sidebar-toolbar-actions">
            <button type="button" title="新建目录" @click="createFolder">＋</button>
            <button type="button" title="收起文件栏" aria-label="收起文件栏" @click="toggleVaultSidebar">‹</button>
          </div>
        </div>
        <div class="sidebar-section">
          <button class="nav-item" :class="{ active: viewFilter === 'all' }" @click="setFilter('all')">⌂ 全部文章 <small>{{ documents.length }}</small></button>
          <button class="nav-item favorites-nav" :class="{ active: viewFilter === 'favorites' }" @click="setFilter('favorites')"><span class="favorite-star">☆</span> 收藏</button>
          <button class="nav-item" :class="{ active: viewFilter === 'recent' }" @click="setFilter('recent')">◷ 最近打开</button>
        </div>
        <div class="sidebar-heading">目录</div>
        <div class="folder-tree">
          <div class="folder-item root-folder-item">
            <button type="button" class="tree-row root-row" :class="{ active: selectedFolderId === 0 }" @click="selectFolder(0)" @dragover.prevent @drop.prevent="dropDocument(0, $event)">
              <span class="folder-chevron" :class="{ collapsed: !isFolderExpanded(0) }" @click.stop="toggleFolderExpanded(0)">{{ hasFolderChildren(0) ? (isFolderExpanded(0) ? '▾' : '▸') : '·' }}</span><span class="folder-name">根目录</span><span class="folder-count">{{ folderArticleCount(0) }}</span>
              <span class="folder-more" title="更多操作" @click.stop="toggleFolderMenu(0)">•••</span>
            </button>
            <div v-if="activeFolderMenuId === 0" class="folder-menu" @click.stop>
              <button type="button" @click="createSubfolder({ id: 0, name: '根目录' })">创建子目录</button>
            </div>
          </div>
          <div v-for="folder in visibleFolders" :key="folder.id" class="folder-item" :style="{ paddingLeft: `${folderDepth(folder) * 16}px` }">
            <button type="button" class="tree-row" :class="{ active: selectedFolderId === folder.id }" @click="selectFolder(folder.id)" @dragover.prevent @drop.prevent="dropDocument(folder.id, $event)">
              <span class="folder-chevron" :class="{ collapsed: !isFolderExpanded(folder.id) }" @click.stop="toggleFolderExpanded(folder.id)">{{ hasFolderChildren(folder.id) ? (isFolderExpanded(folder.id) ? '▾' : '▸') : '·' }}</span><span class="folder-name">{{ folder.name }}</span><span class="folder-count">{{ folderArticleCount(folder.id) }}</span>
              <span class="folder-more" title="更多操作" @click.stop="toggleFolderMenu(folder.id)">•••</span>
            </button>
            <div v-if="activeFolderMenuId === folder.id" class="folder-menu" @click.stop>
              <button type="button" @click="renameFolder(folder)">重命名</button>
              <button type="button" @click="createSubfolder(folder)">创建子目录</button>
              <button type="button" class="danger" @click="removeFolder(folder)">删除</button>
            </div>
          </div>
        </div>
        <div class="sidebar-heading tags-heading">标签</div>
        <div class="tag-list">
          <button v-for="(count, tag) in tags" :key="tag" class="tag-chip" :class="{ active: selectedTag === tag }" @click="selectTag(tag)">#{{ tag }} <small>{{ count }}</small></button>
        </div>
      </aside>

      <main class="workspace-main">
        <div class="mobile-toolbar"><div class="mobile-toolbar-leading"><button type="button" title="文件栏" aria-label="文件栏" @click="toggleMobileSidebar">☰</button><button type="button" class="mobile-close-button" title="返回主对话" aria-label="返回主对话" @click="$emit('close')">×</button></div><span>{{ activeDocument ? activeDocument.title : '全部文章' }}</span><button type="button" title="信息栏" aria-label="信息栏" @click="toggleMobileContext">ⓘ</button></div>
        <div v-if="searchResults.length || isSearching" class="search-results">
          <div class="panel-title"><span>搜索结果</span><button @click="clearSearch">清除</button></div>
          <div v-if="isSearching" class="empty-text">搜索中…</div>
          <button v-for="item in searchResults" :key="item.id" class="search-result" @click="openDocument(item.id)">
            <strong>{{ item.title || item.name }}</strong><small>{{ item.path || item.folder_path }}</small>
            <span v-html="(item.highlights && item.highlights.content && item.highlights.content[0]) || ''"></span>
          </button>
        </div>
        <div v-else-if="viewMode === 'browser'" class="document-browser">
          <div class="browser-heading">
            <h2>{{ selectedTag ? `#${selectedTag}` : selectedFolderName }}<small class="document-count">{{ selectedFolderArticleCount }} 篇</small></h2>
            <div class="browser-heading-actions">
              <label class="document-sort-label">排序
                <select v-model="documentSort" class="document-sort-select" aria-label="文章排序方式">
                  <option value="updated_desc">最近更新</option>
                  <option value="updated_asc">最早更新</option>
                  <option value="title_asc">标题 A-Z</option>
                </select>
              </label>
              <button @click="newDocument">＋ 新建文章</button>
            </div>
          </div>
          <div class="document-grid">
            <article v-for="doc in filteredDocuments" :key="doc.id" class="document-card" draggable="true" role="button" tabindex="0" @dragstart="dragDocument(doc, $event)" @click="openDocument(doc.id)" @keydown.enter.prevent="openDocument(doc.id)" @keydown.space.prevent="openDocument(doc.id)">
              <span class="doc-card-icon">▤</span><strong>{{ doc.title || doc.name }}</strong><small>{{ doc.path || '根目录' }}</small><em>{{ formatTime(doc.updated_at) }}</em>
              <i v-if="doc.favorite" class="favorite-star">★</i>
              <div class="document-card-more" @click.stop>
                <button type="button" class="document-card-more-trigger" :title="`文章“${doc.title || doc.name || '未命名文章'}”的更多操作`" aria-label="更多文章操作" aria-haspopup="menu" :aria-expanded="String(cardMenuDocumentId === doc.id)" @click="toggleCardMenu(doc.id, $event)">
                  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false"><circle cx="4" cy="10" r="1.5" fill="currentColor"/><circle cx="10" cy="10" r="1.5" fill="currentColor"/><circle cx="16" cy="10" r="1.5" fill="currentColor"/></svg>
                </button>
              </div>
            </article>
            <div v-if="filteredDocuments.length === 0" class="empty-text">暂无文档，点击右上角新建文章。</div>
          </div>
        </div>
        <div v-else class="editor-tabs">
          <div class="tab-bar">
            <button v-for="tab in tabs" :key="tab.id" class="editor-tab" :class="{ active: activeTabId === tab.id, dirty: tab.dirty }" @click="activeTabId = tab.id">{{ tab.title }}<span v-if="tab.dirty">•</span><b @click.stop="closeTab(tab.id)">×</b></button>
            <button class="new-tab" @click="newDocument">＋</button>
          </div>
          <div v-if="activeTab" class="editor-toolbar">
            <div class="editor-title-fields">
              <input v-model="activeTab.title" @change="markDirty(true)" placeholder="文章标题" />
              <FolderSelect v-model="activeTab.folderId" :options="folderOptions" title="选择父目录" @change="markDirty(true)" />
            </div>
            <div class="editor-toolbar-actions">
              <span class="save-state">{{ saveState }}</span>
              <button @click="saveDocument" :disabled="saving || !activeTab.dirty">{{ saving ? '保存中…' : '保存' }}</button>
              <div class="editor-more-actions" @click.stop>
                <button
                  type="button"
                  class="editor-more-trigger"
                  title="更多文章操作"
                  aria-label="更多文章操作"
                  aria-haspopup="menu"
                  :aria-expanded="String(moreMenuOpen)"
                  @click="moreMenuOpen = !moreMenuOpen"
                >
                  <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false">
                    <circle cx="4" cy="10" r="1.5" fill="currentColor" />
                    <circle cx="10" cy="10" r="1.5" fill="currentColor" />
                    <circle cx="16" cy="10" r="1.5" fill="currentColor" />
                  </svg>
                </button>
                <div v-if="moreMenuOpen" class="editor-more-menu" role="menu">
                  <button type="button" role="menuitem" :disabled="activeTab.isNew" @click="toggleFavorite">
                    <span class="more-menu-favorite" aria-hidden="true">{{ activeTab.favorite ? '★' : '☆' }}</span>
                    {{ activeTab.favorite ? '取消收藏' : '收藏文章' }}
                  </button>
                  <button type="button" role="menuitem" :disabled="activeTab.isNew || downloadingDocumentId === activeTab.id" @click="downloadActiveDocument">{{ downloadingDocumentId === activeTab.id ? '下载中…' : '下载 Markdown' }}</button>
                  <button type="button" role="menuitem" class="danger" :disabled="activeTab.isNew || saving || pendingImageUploads > 0" @click="deleteActiveDocument">
                    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="M4.5 6h11m-9.5 0 .6 10h6.8l.6-10M8 6V4h4v2m-3 3v4m2-4v4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
                    删除文章
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="activeTab" class="editor-tags">
            <span class="editor-tags-label">标签</span>
            <button v-for="tag in (activeTab.tags || [])" :key="tag" type="button" class="editor-tag" @click="removeTag(tag)">#{{ tag }} <b>×</b></button>
            <input v-model="tagInput" type="text" placeholder="添加标签，回车确认" @keydown.enter.prevent="addTag" />
            <button v-if="tagInput.trim()" type="button" class="editor-tag-add" @click="addTag">添加</button>
            <button v-for="tag in suggestedTags" :key="`suggest-${tag}`" type="button" class="tag-suggestion" @click="addTag(tag)">+ {{ tag }}</button>
          </div>
          <div v-if="activeTab" class="editor-split" :class="{ 'is-resizing': isResizingEditorSplit }" :style="{ '--editor-left-width': `${editorSplitPercent}%` }">
            <div class="markdown-editor-host" :class="{ 'is-fullscreen': isMarkdownEditorFullscreen }" @dragover.prevent @drop.prevent="dropAsset">
              <mavon-editor
                ref="editor"
                v-model="activeTab.content"
                class="markdown-editor"
                :toolbars="markdownToolbars"
                :subfield="false"
                default-open="edit"
                :box-shadow="false"
                :toolbars-flag="true"
                :html="true"
                :autofocus="false"
                placeholder="# 开始记录…"
                @change="markDirty()"
                @imgAdd="handleEditorImageAdd"
                @fullScreen="handleEditorFullscreen"
              />
            </div>
            <button
              type="button"
              class="editor-split-handle"
              role="separator"
              tabindex="0"
              aria-label="调整编辑器与预览区域宽度"
              aria-orientation="vertical"
              :aria-valuenow="editorSplitPercent"
              aria-valuemin="20"
              aria-valuemax="80"
              @pointerdown.stop.prevent="startEditorSplitDrag"
              @keydown.left.prevent="adjustEditorSplit(-2)"
              @keydown.right.prevent="adjustEditorSplit(2)"
            ></button>
            <article class="preview markdown-body" v-html="previewHtml" @click="handlePreviewClick" @error.capture="handlePreviewAssetError"></article>
          </div>
        </div>
      </main>

      <aside class="context-sidebar" :class="{ 'mobile-open': mobileContext, 'is-collapsed': contextCollapsed }">
        <div class="context-toolbar"><span>文章信息</span><button type="button" title="收起信息栏" aria-label="收起信息栏" @click="toggleContextSidebar">›</button></div>
        <template v-if="activeDocument">
          <div class="context-block"><div class="panel-title">大纲</div><button v-for="heading in headings" :key="heading.key" class="outline-row" :style="{ paddingLeft: `${8 + heading.level * 10}px` }" @click="jumpToHeading(heading)">{{ heading.text }}</button><span v-if="!headings.length" class="empty-text">暂无标题</span></div>
          <div class="context-block"><div class="panel-title">属性</div><div v-for="(value, key) in activeDocument.frontmatter" :key="key" class="property-row"><span>{{ key }}</span><code>{{ value }}</code></div><span v-if="!Object.keys(activeDocument.frontmatter || {}).length" class="empty-text">未设置 frontmatter</span></div>
          <div class="context-block"><div class="panel-title">反向链接 <small>{{ backlinks.length }}</small></div><button v-for="item in backlinks" :key="item.id" class="backlink-row" @click="openDocument(item.document && item.document.id)">{{ item.document ? item.document.title : item.target_path }}</button><span v-if="!backlinks.length" class="empty-text">暂无反向链接</span></div>
          <div class="context-block"><div class="panel-title">版本</div><button v-for="revision in revisions" :key="revision.version" class="revision-row" @click="restoreRevision(revision.version)">v{{ revision.version }} · {{ formatTime(revision.created_at) }}</button></div>
        </template>
        <div class="context-block assets-block">
          <div class="panel-title"><span>附件</span><button type="button" @click="$refs.assetInput && $refs.assetInput.click()">上传</button></div>
          <input ref="assetInput" class="asset-input" type="file" multiple @change="handleAssetPicker">
          <div class="asset-quota"><span>{{ formatBytes(assetUsage.used_bytes) }} / {{ formatBytes(assetUsage.quota_bytes) }}</span><span>{{ assetUsage.used_percent }}%</span></div>
          <div class="quota-track"><i :style="{ width: `${Math.min(100, Number(assetUsage.used_percent || 0))}%` }"></i></div>
          <button v-for="asset in assets" :key="`asset-${asset.id}`" type="button" class="asset-row" @click="insertAsset(asset)">
            <span class="asset-kind">{{ assetKind(asset) }}</span><span class="asset-name" :title="asset.original_name">{{ asset.original_name }}</span><small>{{ formatBytes(asset.size) }}</small><b class="asset-remove" title="删除附件" @click.stop="deleteAsset(asset)">×</b>
          </button>
          <span v-if="!assets.length" class="empty-text">暂无附件</span>
        </div>
        <div v-if="!activeDocument" class="context-empty">选择一篇文章查看大纲、属性和反向链接。</div>
      </aside>
    </div>

    <div v-if="cardMenuDocument" class="document-card-menu" role="menu" :style="cardMenuPosition" @click.stop>
      <button type="button" role="menuitem" @click="toggleDocumentFavorite(cardMenuDocument)"><span class="more-menu-favorite" aria-hidden="true">{{ cardMenuDocument.favorite ? '★' : '☆' }}</span>{{ cardMenuDocument.favorite ? '取消收藏' : '收藏文章' }}</button>
      <button type="button" role="menuitem" :disabled="downloadingDocumentId === cardMenuDocument.id" @click="downloadDocumentFromCard(cardMenuDocument)">{{ downloadingDocumentId === cardMenuDocument.id ? '下载中…' : '下载 Markdown' }}</button>
      <button type="button" role="menuitem" class="danger" @click="deleteDocumentFromCard(cardMenuDocument)"><svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="M4.5 6h11m-9.5 0 .6 10h6.8l.6-10M8 6V4h4v2m-3 3v4m2-4v4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>删除文章</button>
    </div>

    <div v-if="commandPaletteOpen" class="command-overlay" @click.self="commandPaletteOpen = false">
      <div class="command-palette"><input ref="commandInput" v-model="commandText" placeholder="输入命令…" @keydown.enter="runCommand" /><button @click="newDocument">新建文章</button><button @click="createFolder">新建目录</button><button @click="runSearch">搜索当前 Vault</button></div>
    </div>
  </section>
</template>

<script>
import MarkdownIt from 'markdown-it';
import { mavonEditor } from 'mavon-editor';
import FolderSelect from './FolderSelect.vue';
import 'mavon-editor/dist/css/index.css';
import {
  getKnowledgeWorkspace, getKnowledgeDocument, createKnowledgeDocument, updateKnowledgeDocument, moveKnowledgeDocument,
  getKnowledgeLinks, getKnowledgeRevisions, restoreKnowledgeRevision, toggleKnowledgeFavorite, deleteKnowledgeDocument,
  createKnowledgeFolder, updateKnowledgeFolder, deleteKnowledgeFolder, searchKnowledgeWorkspace, uploadKnowledgeAsset, resolveKnowledgeAssets, deleteKnowledgeAsset,
  downloadKnowledgeDocument, downloadKnowledgeArchive, convertKnowledgeFileToMarkdown
} from '@/api/kb';
import { showPrompt, showConfirm } from '@/utils/dialog';

const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

export default {
  name: 'KnowledgeWorkspace',
  components: { mavonEditor, FolderSelect },
  props: { ck: { type: String, default: '' }, creationPreset: { type: Object, default: null }, creationPresetKey: { type: Number, default: 0 } },
  data() {
    return {
      loading: true,
      workspaceBootstrapSucceeded: false,
      saving: false,
      saveState: '',
      documents: [],
      folders: [],
      tags: {},
      assets: [],
      assetUsage: { used_bytes: 0, quota_bytes: 50 * 1024 * 1024, remaining_bytes: 50 * 1024 * 1024, used_percent: 0 },
      tabs: [],
      activeTabId: null,
      viewMode: 'browser',
      selectedFolderId: 0,
      selectedTag: '',
      viewFilter: 'all',
      documentSort: 'updated_desc',
      searchText: '',
      tagInput: '',
      searchResults: [],
      isSearching: false,
      mobileSidebar: false,
      mobileContext: false,
      vaultCollapsed: false,
      contextCollapsed: false,
      commandPaletteOpen: false,
      moreMenuOpen: false,
      cardMenuDocumentId: null,
      cardMenuPosition: {},
      downloadingDocumentId: null,
      downloadingArchive: false,
      importingDocuments: false,
      importProgress: '',
      commandText: '',
      backlinks: [],
      revisions: [],
      saveTimer: null,
      handledUploads: [],
      activeFolderMenuId: null,
      expandedFolders: { 0: true },
      pendingImageUploads: 0,
      previewHtml: '',
      assetUrlCache: {},
      assetResolveTimer: null,
      assetRefreshTimer: null,
      assetErrorRetryAt: 0,
      previewRequestId: 0,
      editorSplitPercent: 50,
      isResizingEditorSplit: false,
      isMarkdownEditorFullscreen: false,
      restoringWorkspaceState: false,
      workspacePersistTimer: null,
      markdownToolbars: {
        bold: true,
        italic: true,
        header: true,
        strikethrough: true,
        quote: true,
        ol: true,
        ul: true,
        link: true,
        imagelink: true,
        code: true,
        table: true,
        fullscreen: true,
        undo: true,
        redo: true,
        trash: false,
        save: false,
        navigation: false,
        readmodel: false,
        htmlcode: false,
        help: false,
        alignleft: false,
        aligncenter: false,
        alignright: false,
        subfield: false,
        preview: false
      }
    };
  },
  computed: {
    cardMenuDocument() { return this.documents.find(doc => doc.id === this.cardMenuDocumentId) || null; },
    visibleFolders() {
      const rows = [];
      const append = (parentId, depth) => {
        this.folders
          .filter(folder => Number(folder.parent_id || 0) === Number(parentId))
          .forEach(folder => {
            rows.push({ ...folder, _depth: depth });
            if (this.isFolderExpanded(folder.id)) append(folder.id, depth + 1);
          });
      };
      if (this.isFolderExpanded(0)) append(0, 1);
      return rows;
    },
    folderOptions() {
      const rows = [];
      const append = (parentId, depth) => {
        this.folders.filter(folder => Number(folder.parent_id || 0) === Number(parentId)).forEach(folder => {
          rows.push({ ...folder, _depth: depth });
          append(folder.id, depth + 1);
        });
      };
      append(0, 1);
      return rows;
    },
    activeTab() { return this.tabs.find(tab => tab.id === this.activeTabId) || null; },
    activeDocument() { return this.activeTab ? this.documents.find(doc => Number(doc.id) === Number(this.activeTab.id)) : null; },
    documentImportLabel() { return this.importingDocuments ? (this.importProgress ? `导入中… ${this.importProgress}` : '导入中…') : '导入文档'; },
    selectedFolderName() { const folder = this.folders.find(item => Number(item.id) === Number(this.selectedFolderId)); return folder ? folder.name : '全部文章'; },
    selectedFolderArticleCount() { return this.selectedFolderId ? this.documents.filter(doc => Number(doc.folder_id) === Number(this.selectedFolderId)).length : this.documents.length; },
    filteredDocuments() {
      let rows = this.documents.slice();
      if (this.selectedFolderId) rows = rows.filter(doc => Number(doc.folder_id) === Number(this.selectedFolderId));
      if (this.selectedTag) rows = rows.filter(doc => (doc.tags || []).includes(this.selectedTag));
      if (this.viewFilter === 'favorites') rows = rows.filter(doc => doc.favorite);
      if (this.viewFilter === 'recent') rows = rows.sort((a, b) => new Date(b.last_opened_at || 0) - new Date(a.last_opened_at || 0));
      else rows.sort((a, b) => {
        if (this.documentSort === 'title_asc') return String(a.title || a.name || '').localeCompare(String(b.title || b.name || ''), 'zh-CN');
        const timestamp = value => {
          if (typeof value === 'number' || /^\d+$/.test(String(value || ''))) {
            const numeric = Number(value) || 0;
            return numeric && numeric < 1e12 ? numeric * 1000 : numeric;
          }
          const parsed = Date.parse(value || '');
          return Number.isFinite(parsed) ? parsed : 0;
        };
        const dateOf = document => timestamp(document.updated_at || document.created_at);
        return this.documentSort === 'updated_asc' ? dateOf(a) - dateOf(b) : dateOf(b) - dateOf(a);
      });
      return rows;
    },
    headings() { if (!this.activeTab) return []; const list = []; (this.activeTab.content || '').split(/\r?\n/).forEach((line, index) => { const match = /^(#{1,6})\s+(.+)$/.exec(line); if (match) list.push({ key: `${index}-${match[2]}`, line: index, level: match[1].length, text: match[2].trim() }); }); return list; },
    suggestedTags() { const current = new Set((this.activeTab && this.activeTab.tags) || []); return Object.keys(this.tags).filter(tag => !current.has(tag) && (!this.tagInput.trim() || tag.toLowerCase().includes(this.tagInput.trim().toLowerCase()))).slice(0, 6); }
  },
  watch: {
    activeTabId() { this.loadContext(); this.scheduleAssetPreview(); this.persistWorkspaceState(); },
    creationPresetKey() { this.applyPreset(); },
    selectedFolderId() { this.persistWorkspaceState(); },
    selectedTag() { this.persistWorkspaceState(); },
    viewFilter() { this.persistWorkspaceState(); },
    viewMode() { this.persistWorkspaceState(); },
    vaultCollapsed() { this.persistWorkspaceState(); },
    contextCollapsed() { this.persistWorkspaceState(); },
    expandedFolders: { deep: true, handler() { this.persistWorkspaceState(); } },
    tabs: { deep: true, handler() { this.persistWorkspaceState(); } }
  },
  async mounted() {
    window.addEventListener('aihelp-knowledge-upload-file', this.handleHostUpload); window.__aihelpUploadKnowledgeFile = this.handleHostUpload; window.__aihelpKnowledgePanelReady = true; window.dispatchEvent(new CustomEvent('aihelp-knowledge-panel-ready'));
    window.addEventListener('beforeunload', this.beforeUnload);
    if (this.ck) { await this.bootstrap(); await this.restoreWorkspaceState(); } this.applyPreset();
  },
  beforeDestroy() { this.stopEditorSplitDrag(); this.persistWorkspaceState(true); window.removeEventListener('aihelp-knowledge-upload-file', this.handleHostUpload); if (window.__aihelpUploadKnowledgeFile === this.handleHostUpload) delete window.__aihelpUploadKnowledgeFile; window.__aihelpKnowledgePanelReady = false; window.removeEventListener('beforeunload', this.beforeUnload); if (this.saveTimer) clearTimeout(this.saveTimer); if (this.assetResolveTimer) clearTimeout(this.assetResolveTimer); if (this.assetRefreshTimer) clearTimeout(this.assetRefreshTimer); if (this.workspacePersistTimer) clearTimeout(this.workspacePersistTimer); },
  methods: {
    startEditorSplitDrag(event) {
      if (event.button !== undefined && event.button !== 0) return;
      this.isResizingEditorSplit = true;
      window.addEventListener('pointermove', this.onEditorSplitDrag);
      window.addEventListener('pointerup', this.stopEditorSplitDrag);
      window.addEventListener('pointercancel', this.stopEditorSplitDrag);
      this.onEditorSplitDrag(event);
    },
    onEditorSplitDrag(event) {
      if (!this.isResizingEditorSplit) return;
      const split = this.$el.querySelector('.editor-split');
      if (!split) return;
      const bounds = split.getBoundingClientRect();
      if (!bounds.width) return;
      const percent = ((event.clientX - bounds.left) / bounds.width) * 100;
      this.editorSplitPercent = Math.max(20, Math.min(80, percent));
    },
    stopEditorSplitDrag() {
      this.isResizingEditorSplit = false;
      window.removeEventListener('pointermove', this.onEditorSplitDrag);
      window.removeEventListener('pointerup', this.stopEditorSplitDrag);
      window.removeEventListener('pointercancel', this.stopEditorSplitDrag);
    },
    adjustEditorSplit(amount) {
      this.editorSplitPercent = Math.max(20, Math.min(80, this.editorSplitPercent + amount));
    },
    handleEditorFullscreen(status) {
      this.isMarkdownEditorFullscreen = !!status;
    },
    workspaceStorageKey() {
      const value = String(this.ck || 'anonymous');
      let hash = 2166136261;
      for (let index = 0; index < value.length; index += 1) { hash ^= value.charCodeAt(index); hash = Math.imul(hash, 16777619); }
      return `aihelp_knowledge_workspace_${(hash >>> 0).toString(36)}`;
    },
    persistWorkspaceState(immediate = false) {
      if (this.restoringWorkspaceState || !this.ck || typeof localStorage === 'undefined') return;
      const write = () => {
        try {
          const snapshot = {
            version: 1,
            activeTabId: this.activeTabId,
            viewMode: this.viewMode,
            selectedFolderId: this.selectedFolderId,
            selectedTag: this.selectedTag,
            viewFilter: this.viewFilter,
            vaultCollapsed: !!this.vaultCollapsed,
            contextCollapsed: !!this.contextCollapsed,
            expandedFolders: { ...this.expandedFolders },
            tabs: this.tabs.map(tab => ({
              id: tab.id, title: tab.title, content: String(tab.content || ''), savedContent: String(tab.savedContent || ''),
              tags: tab.tags || [], folderId: Number(tab.folderId || 0), dirty: !!tab.dirty,
              isNew: !!tab.isNew, version: Number(tab.version || 0), favorite: Number(tab.favorite || 0)
            })),
            savedAt: Date.now()
          };
          localStorage.setItem(this.workspaceStorageKey(), JSON.stringify(snapshot));
        } catch (error) { /* Storage quota/private mode should not block editing. */ }
      };
      if (immediate) write();
      else { if (this.workspacePersistTimer) clearTimeout(this.workspacePersistTimer); this.workspacePersistTimer = setTimeout(write, 180); }
    },
    async restoreWorkspaceState() {
      if (!this.ck || typeof localStorage === 'undefined') return;
      let snapshot;
      try { snapshot = JSON.parse(localStorage.getItem(this.workspaceStorageKey()) || 'null'); } catch (error) { snapshot = null; }
      if (!snapshot || !Array.isArray(snapshot.tabs) || !snapshot.tabs.length) return;
      this.restoringWorkspaceState = true;
      try {
        const known = new Set(this.documents.map(doc => String(doc.id)));
        const cachedTabs = snapshot.tabs.filter(tab => tab && (tab.isNew || !this.workspaceBootstrapSucceeded || known.has(String(tab.id)))).map(tab => ({
          id: tab.id, title: tab.title || '未命名文章', content: String(tab.content || ''), savedContent: String(tab.savedContent || ''),
          tags: Array.isArray(tab.tags) ? tab.tags : [], folderId: Number(tab.folderId || 0), dirty: !!tab.dirty,
          isNew: !!tab.isNew, version: Number(tab.version || 0), favorite: Number(tab.favorite || 0)
        }));
        this.tabs = cachedTabs;
        this.viewMode = snapshot.viewMode === 'editor' && cachedTabs.length ? 'editor' : 'browser';
        this.selectedFolderId = Number(snapshot.selectedFolderId || 0);
        this.selectedTag = snapshot.selectedTag || '';
        this.viewFilter = snapshot.viewFilter || 'all';
        this.vaultCollapsed = !!snapshot.vaultCollapsed;
        this.contextCollapsed = !!snapshot.contextCollapsed;
        if (snapshot.expandedFolders && typeof snapshot.expandedFolders === 'object') this.expandedFolders = { ...snapshot.expandedFolders };
        this.activeTabId = cachedTabs.some(tab => String(tab.id) === String(snapshot.activeTabId)) ? snapshot.activeTabId : (cachedTabs[0] && cachedTabs[0].id);
      } finally { this.restoringWorkspaceState = false; }
      this.scheduleAssetPreview();
      await this.loadContext();
    },
    async bootstrap() { this.loading = true; this.workspaceBootstrapSucceeded = false; try { const res = await getKnowledgeWorkspace(this.ck); if (res.code === 0 || res.code === 200) { this.documents = this.uniqueDocuments(res.data.documents || []); this.folders = res.data.folders || []; this.tags = res.data.tags || {}; this.assets = res.data.assets || []; this.assetUsage = res.data.asset_usage || this.assetUsage; this.workspaceBootstrapSucceeded = true; } } catch (error) { this.notify(error.message || '知识库加载失败'); } finally { this.loading = false; } },
    uniqueDocuments(rows) { const seen = new Set(); return (rows || []).filter(row => { const id = String(row && row.id != null ? row.id : ''); if (!id || seen.has(id)) return false; seen.add(id); return true; }); },
    hasFolderChildren(id) { return this.folders.some(folder => Number(folder.parent_id || 0) === Number(id)); },
    folderArticleCount(id) { return this.documents.filter(doc => Number(doc.folder_id || 0) === Number(id)).length; },
    isFolderExpanded(id) { return this.expandedFolders[id] !== false; },
    toggleFolderExpanded(id) { if (this.isFolderExpanded(id)) this.$set(this.expandedFolders, id, false); else this.$set(this.expandedFolders, id, true); },
    folderDepth(folder) { if (folder && folder._depth) return Number(folder._depth); let depth = 1; let parent = Number(folder.parent_id); while (parent && depth < 20) { const item = this.folders.find(row => Number(row.id) === parent); if (!item) break; depth += 1; parent = Number(item.parent_id); } return depth; },
    setFilter(filter) { this.viewFilter = filter; this.selectedTag = ''; this.selectedFolderId = 0; this.viewMode = 'browser'; this.mobileSidebar = false; },
    selectFolder(id) { this.selectedFolderId = Number(id); this.selectedTag = ''; this.viewFilter = 'all'; this.viewMode = 'browser'; this.mobileSidebar = false; },
    selectTag(tag) { this.selectedTag = tag; this.selectedFolderId = 0; this.viewFilter = 'all'; this.viewMode = 'browser'; this.mobileSidebar = false; },
    toggleVaultSidebar() {
      this.vaultCollapsed = !this.vaultCollapsed;
      if (this.vaultCollapsed) this.mobileSidebar = false;
    },
    toggleContextSidebar() {
      this.contextCollapsed = !this.contextCollapsed;
      if (this.contextCollapsed) this.mobileContext = false;
    },
    toggleMobileSidebar() {
      this.vaultCollapsed = false;
      this.mobileSidebar = !this.mobileSidebar;
    },
    toggleMobileContext() {
      this.contextCollapsed = false;
      this.mobileContext = !this.mobileContext;
    },
    closeFolderMenu() { this.activeFolderMenuId = null; this.moreMenuOpen = false; this.cardMenuDocumentId = null; },
    toggleCardMenu(id, event) {
      if (this.cardMenuDocumentId === id) { this.cardMenuDocumentId = null; return; }
      const rect = event.currentTarget.getBoundingClientRect();
      const menuWidth = 156;
      const menuHeight = 126;
      const margin = 8;
      const belowTop = rect.bottom + 5;
      const aboveTop = rect.top - menuHeight - 5;
      const top = belowTop + menuHeight <= window.innerHeight - margin
        ? belowTop
        : aboveTop >= margin ? aboveTop : margin;
      const left = Math.max(margin, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - margin));
      this.cardMenuPosition = { position: 'fixed', top: `${Math.max(margin, top)}px`, left: `${left}px` };
      this.cardMenuDocumentId = id;
    },
    toggleFolderMenu(id) { this.activeFolderMenuId = this.activeFolderMenuId === id ? null : id; },
    async createFolder() { const name = await showPrompt('目录名称'); if (!name || !name.trim()) return; const res = await createKnowledgeFolder(this.ck, name.trim(), this.selectedFolderId); if (res.code === 0 || res.code === 200) await this.bootstrap(); else this.notify(res.message || '目录创建失败'); },
    async renameFolder(folder) { this.closeFolderMenu(); const name = await showPrompt('重命名目录', folder.name); if (!name || !name.trim() || name.trim() === folder.name) return; const res = await updateKnowledgeFolder(this.ck, folder.id, { name: name.trim() }); if (res.code === 0 || res.code === 200) await this.bootstrap(); else this.notify(res.message || '目录重命名失败'); },
    async createSubfolder(folder) { this.closeFolderMenu(); const name = await showPrompt(`在“${folder.name}”下创建子目录`); if (!name || !name.trim()) return; const res = await createKnowledgeFolder(this.ck, name.trim(), folder.id); if (res.code === 0 || res.code === 200) await this.bootstrap(); else this.notify(res.message || '子目录创建失败'); },
    async removeFolder(folder) { this.closeFolderMenu(); if (!await showConfirm(`确定删除目录“${folder.name}”？目录必须为空才能删除。`, { dangerous: true, confirmText: '删除' })) return; const res = await deleteKnowledgeFolder(this.ck, folder.id); if (res.code === 0 || res.code === 200) { if (Number(this.selectedFolderId) === Number(folder.id)) this.selectedFolderId = 0; await this.bootstrap(); } else this.notify(res.message || '目录删除失败'); },
    newDocument() { const id = `new-${Date.now()}`; this.viewMode = 'editor'; this.tabs.push({ id, title: '未命名文章', content: '# 未命名文章\n\n', savedContent: '', tags: [], folderId: Number(this.selectedFolderId || 0), dirty: true, isNew: true, version: 0, favorite: 0 }); this.tagInput = ''; this.activeTabId = id; this.searchResults = []; this.mobileSidebar = false; this.scheduleAssetPreview(); },
    async openDocument(id) { if (!id) return; this.viewMode = 'editor'; const existing = this.tabs.find(tab => Number(tab.id) === Number(id)); if (existing) { this.activeTabId = existing.id; return; } const res = await getKnowledgeDocument(this.ck, id); if (res.code !== 0 && res.code !== 200) return this.notify(res.message || '文章加载失败'); const doc = res.data; const loaded = this.tabs.find(tab => Number(tab.id) === Number(doc.id)); if (loaded) { this.activeTabId = loaded.id; return; } this.documents = this.documents.map(item => Number(item.id) === Number(id) ? { ...item, ...doc } : item); this.tabs.push({ id: doc.id, title: doc.title, content: doc.content || '', savedContent: doc.content || '', tags: doc.tags || [], folderId: Number(doc.folder_id || 0), dirty: false, version: doc.version, favorite: doc.favorite, isNew: false }); this.tagInput = ''; this.activeTabId = doc.id; this.searchResults = []; this.mobileSidebar = false; this.scheduleAssetPreview(); },
    async closeTab(id, force = false) { const index = this.tabs.findIndex(tab => tab.id === id); if (index < 0) return; if (!force && this.tabs[index].dirty && !await showConfirm('文章尚未保存，确定关闭吗？')) return; this.tabs.splice(index, 1); if (this.activeTabId === id) this.activeTabId = this.tabs[index]?.id || this.tabs[index - 1]?.id || null; if (!this.tabs.length) this.viewMode = 'browser'; },
    markDirty(force = false) { if (!this.activeTab) return; const normalized = this.normalizeAssetUrls(this.activeTab.content); if (normalized !== this.activeTab.content) this.activeTab.content = normalized; if (!force && !this.activeTab.dirty && !this.activeTab.isNew && this.activeTab.savedContent === this.activeTab.content) return; this.activeTab.dirty = true; this.saveState = '未保存'; this.scheduleAssetPreview(); if (this.saveTimer) clearTimeout(this.saveTimer); this.saveTimer = setTimeout(() => this.saveDocument(), 2000); },
    async saveDocument() {
      if (!this.activeTab) return false;
      if (!this.activeTab.dirty) return true;
      if (this.saving || this.pendingImageUploads > 0) return false;
      this.saving = true;
      this.saveState = '保存中…';
      const tab = this.activeTab;
      let succeeded = false;
      try {
        let res;
        const folderId = Number(tab.folderId || 0);
        if (tab.isNew) res = await createKnowledgeDocument(this.ck, { title: tab.title, content: tab.content, folder_id: folderId });
        else res = await updateKnowledgeDocument(this.ck, tab.id, { title: tab.title, content: tab.content, folder_id: folderId, version: tab.version });
        if (res.code === 0 || res.code === 200) {
          const doc = res.data;
          const duplicateIndex = this.tabs.findIndex(row => row !== tab && Number(row.id) === Number(doc.id));
          if (duplicateIndex >= 0) this.tabs.splice(duplicateIndex, 1);
          tab.id = doc.id;
          tab.version = doc.version;
          tab.folderId = Number(doc.folder_id || 0);
          tab.isNew = false;
          tab.dirty = false;
          tab.favorite = doc.favorite;
          tab.title = doc.title;
          tab.tags = doc.tags || tab.tags || [];
          tab.savedContent = tab.content;
          this.documents = this.documents.some(item => Number(item.id) === Number(doc.id)) ? this.documents.map(item => Number(item.id) === Number(doc.id) ? { ...item, ...doc } : item) : [doc, ...this.documents];
          this.activeTabId = doc.id;
          this.saveState = '已保存';
          succeeded = true;
          await this.bootstrap();
          await this.loadContext();
        } else {
          this.saveState = res.code === 409 ? '版本冲突，请刷新' : (res.message || '保存失败');
        }
      } catch (error) {
        this.saveState = error.message || '保存失败';
      } finally {
        this.saving = false;
      }
      return succeeded;
    },
    downloadFilename(value, fallback) {
      const clean = String(value || '').replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').trim().replace(/[. ]+$/, '');
      return clean || fallback;
    },
    saveBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },
    async saveAllDirtyDocuments() {
      const dirtyTabs = this.tabs.filter(tab => tab.dirty);
      if (!dirtyTabs.length) return true;
      const activeTab = this.activeTab;
      if (this.saveTimer) { clearTimeout(this.saveTimer); this.saveTimer = null; }
      for (const tab of dirtyTabs) {
        this.activeTabId = tab.id;
        await this.$nextTick();
        if (!await this.saveDocument()) {
          this.activeTabId = activeTab && this.tabs.includes(activeTab) ? activeTab.id : null;
          return false;
        }
      }
      this.activeTabId = activeTab && this.tabs.includes(activeTab) ? activeTab.id : null;
      return true;
    },
    async downloadDocumentById(id, title = '文章') {
      if (!id || this.downloadingDocumentId !== null || this.downloadingArchive) return;
      const tab = this.tabs.find(item => Number(item.id) === Number(id));
      const previousActiveId = this.activeTabId;
      this.downloadingDocumentId = id;
      try {
        if (tab && tab.dirty) {
          this.activeTabId = tab.id;
          await this.$nextTick();
          if (!await this.saveDocument()) throw new Error('文章保存失败，已取消下载');
          title = tab.title || title;
        }
        const blob = await downloadKnowledgeDocument(this.ck, id);
        const baseTitle = String(title || '').replace(/\.md$/i, '');
        this.saveBlob(blob, `${this.downloadFilename(baseTitle, '文章')}.md`);
      } catch (error) {
        this.notify(error.message || '文章下载失败');
      } finally {
        if (this.activeTabId !== previousActiveId) this.activeTabId = previousActiveId;
        this.downloadingDocumentId = null;
      }
    },
    async downloadDocumentFromCard(doc) {
      this.cardMenuDocumentId = null;
      if (!doc) return;
      await this.downloadDocumentById(doc.id, doc.title || doc.name || '文章');
    },
    async downloadActiveDocument() {
      this.moreMenuOpen = false;
      if (!this.activeTab || this.activeTab.isNew) return;
      await this.downloadDocumentById(this.activeTab.id, this.activeTab.title || '文章');
    },
    async downloadArchive() {
      if (this.downloadingArchive || this.downloadingDocumentId !== null) return;
      this.downloadingArchive = true;
      try {
        if (!await this.saveAllDirtyDocuments()) throw new Error('存在未保存文章，已取消打包下载');
        const blob = await downloadKnowledgeArchive(this.ck);
        const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, '').replace('T', '-');
        this.saveBlob(blob, `知识库-${stamp}.zip`);
      } catch (error) {
        this.notify(error.message || '知识库打包下载失败');
      } finally {
        this.downloadingArchive = false;
      }
    },
    normaliseTag(value) { return String(value || '').trim().replace(/^#+/, '').replace(/\s+/g, '-').slice(0, 64); },
    tagsInContent(content) { const match = /^---\s*\r?\n([\s\S]*?)\r?\n---\s*(?:\r?\n|$)/.exec(String(content || '')); const tags = []; if (match) { const line = match[1].split(/\r?\n/).find(item => /^\s*tags\s*:/i.test(item)); if (line) { const raw = line.replace(/^\s*tags\s*:\s*/i, '').trim(); try { const parsed = JSON.parse(raw); if (Array.isArray(parsed)) tags.push(...parsed); } catch (e) { tags.push(...raw.replace(/^\[|\]$/g, '').split(',').map(item => item.replace(/["']/g, '').trim())); } } } return [...new Set(tags.map(tag => this.normaliseTag(tag)).filter(Boolean))]; },
    setTagsInContent(content, tags) { const values = [...new Set((tags || []).map(tag => this.normaliseTag(tag)).filter(Boolean))]; const line = `tags: ${JSON.stringify(values)}`; const source = String(content || ''); const frontmatter = /^---\s*\r?\n([\s\S]*?)\r?\n---\s*(?:\r?\n|$)/.exec(source); if (frontmatter) { let block = frontmatter[1]; if (/^[ \t]*tags\s*:/im.test(block)) block = block.replace(/^[ \t]*tags\s*:.*$/im, line); else block += `\n${line}`; return `---\n${block}\n---\n${source.slice(frontmatter[0].length)}`; } return `---\n${line}\n---\n\n${source}`; },
    addTag(value = null) { if (!this.activeTab) return; const tag = this.normaliseTag(value === null ? this.tagInput : value); if (!tag) return; const tags = [...new Set([...(this.activeTab.tags || this.tagsInContent(this.activeTab.content)), tag])]; this.activeTab.tags = tags; this.activeTab.content = this.setTagsInContent(this.activeTab.content, tags); this.tagInput = ''; this.markDirty(); },
    removeTag(tag) { if (!this.activeTab) return; const target = this.normaliseTag(tag); const tags = (this.activeTab.tags || this.tagsInContent(this.activeTab.content)).filter(item => item !== target); const escaped = target.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); this.activeTab.content = this.activeTab.content.replace(new RegExp(`(^|\\s)#${escaped}(?=[\\s.,!?;:，。！？；：]|$)`, 'gu'), '$1'); this.activeTab.tags = tags; this.activeTab.content = this.setTagsInContent(this.activeTab.content, tags); this.markDirty(); },
    dragDocument(doc, event) { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/knowledge-document', JSON.stringify({ id: doc.id, version: doc.version })); },
    async dropDocument(folderId, event) { const raw = event.dataTransfer.getData('text/knowledge-document'); if (!raw) return; const item = JSON.parse(raw); const res = await moveKnowledgeDocument(this.ck, item.id, folderId, item.version); if (res.code === 0 || res.code === 200) { const tab = this.tabs.find(row => Number(row.id) === Number(item.id)); if (tab) { tab.folderId = Number(folderId || 0); tab.version = res.data?.version || tab.version; } await this.bootstrap(); } else this.notify(res.message || '移动失败'); },
    async toggleFavorite() { this.moreMenuOpen = false; if (!this.activeTab || this.activeTab.isNew) return; const value = !this.activeTab.favorite; const res = await toggleKnowledgeFavorite(this.ck, this.activeTab.id, value); if (res.code === 0 || res.code === 200) { this.activeTab.favorite = value ? 1 : 0; this.documents = this.documents.map(doc => Number(doc.id) === Number(this.activeTab.id) ? { ...doc, favorite: value ? 1 : 0 } : doc); } else this.notify(res.message || (value ? '收藏失败' : '取消收藏失败')); },
    async toggleDocumentFavorite(doc) {
      this.cardMenuDocumentId = null;
      if (!doc) return;
      const value = !doc.favorite;
      try {
        const res = await toggleKnowledgeFavorite(this.ck, doc.id, value);
        if (res.code === 0 || res.code === 200) {
          const favorite = value ? 1 : 0;
          this.documents = this.documents.map(item => Number(item.id) === Number(doc.id) ? { ...item, favorite } : item);
          const tab = this.tabs.find(item => Number(item.id) === Number(doc.id));
          if (tab) tab.favorite = favorite;
        } else this.notify(res.message || (value ? '收藏失败' : '取消收藏失败'));
      } catch (error) { this.notify(error.message || (value ? '收藏失败' : '取消收藏失败')); }
    },
    async deleteDocumentFromCard(doc) {
      this.cardMenuDocumentId = null;
      if (!doc || this.saving || this.pendingImageUploads > 0) return;
      const tab = this.tabs.find(item => Number(item.id) === Number(doc.id));
      const warning = tab && tab.dirty ? '文章有未保存的修改，删除后也会一并丢弃。' : '';
      const confirmed = await showConfirm(`${warning}${warning ? '\n' : ''}确定删除文章“${doc.title || doc.name || '未命名文章'}”？此操作无法撤销。`, {
        title: '删除文章', confirmText: '删除', dangerous: true
      });
      if (!confirmed) return;
      const wasActive = tab && Number(this.activeTabId) === Number(tab.id);
      if (wasActive && this.saveTimer) { clearTimeout(this.saveTimer); this.saveTimer = null; }
      try {
        const res = await deleteKnowledgeDocument(this.ck, doc.id);
        if (res.code !== 0 && res.code !== 200) {
          this.notify(res.message || '文章删除失败');
          if (wasActive && tab.dirty) this.saveTimer = setTimeout(() => this.saveDocument(), 2000);
          return;
        }
        this.documents = this.documents.filter(item => Number(item.id) !== Number(doc.id));
        if (tab) {
          const tabIndex = this.tabs.indexOf(tab);
          this.tabs.splice(tabIndex, 1);
          if (wasActive) {
            if (this.tabs.length) this.activeTabId = this.tabs[Math.min(tabIndex, this.tabs.length - 1)].id;
            else { this.activeTabId = null; this.viewMode = 'browser'; }
            this.backlinks = [];
            this.revisions = [];
          }
        }
        await this.bootstrap();
      } catch (error) {
        this.notify(error.message || '文章删除失败');
        if (wasActive && tab && tab.dirty) this.saveTimer = setTimeout(() => this.saveDocument(), 2000);
      }
    },
    async deleteActiveDocument() {
      this.moreMenuOpen = false;
      const tab = this.activeTab;
      if (!tab || tab.isNew || this.saving || this.pendingImageUploads > 0) return;
      const warning = tab.dirty ? '文章有未保存的修改，删除后也会一并丢弃。' : '';
      const confirmed = await showConfirm(`${warning}${warning ? '\n' : ''}确定删除文章“${tab.title || '未命名文章'}”？此操作无法撤销。`, {
        title: '删除文章',
        confirmText: '删除',
        dangerous: true
      });
      if (!confirmed) return;
      if (this.saveTimer) { clearTimeout(this.saveTimer); this.saveTimer = null; }
      try {
        const res = await deleteKnowledgeDocument(this.ck, tab.id);
        if (res.code !== 0 && res.code !== 200) {
          this.notify(res.message || '文章删除失败');
          if (tab.dirty) this.saveTimer = setTimeout(() => this.saveDocument(), 2000);
          return;
        }
        const tabIndex = this.tabs.findIndex(item => item === tab);
        this.tabs.splice(tabIndex, 1);
        this.documents = this.documents.filter(doc => Number(doc.id) !== Number(tab.id));
        if (this.tabs.length) this.activeTabId = this.tabs[Math.min(tabIndex, this.tabs.length - 1)].id;
        else { this.activeTabId = null; this.viewMode = 'browser'; }
        this.backlinks = [];
        this.revisions = [];
        await this.bootstrap();
      } catch (error) { this.notify(error.message || '文章删除失败'); }
    },
    async loadContext() { if (!this.activeTab || this.activeTab.isNew) { this.backlinks = []; this.revisions = []; return; } const [backlinks, revisions] = await Promise.all([getKnowledgeLinks(this.ck, this.activeTab.id, 'backlinks'), getKnowledgeRevisions(this.ck, this.activeTab.id)]); this.backlinks = backlinks.data?.items || []; this.revisions = revisions.data?.items || []; },
    async restoreRevision(version) { if (!this.activeTab || !await showConfirm(`恢复到 v${version}？`)) return; const id = this.activeTab.id; const res = await restoreKnowledgeRevision(this.ck, id, version); if (res.code === 0 || res.code === 200) { await this.closeTab(id, true); await this.bootstrap(); await this.openDocument(id); } else this.notify(res.message || '恢复失败'); },
    assetReferencesInContent(content) {
      const source = String(content || '');
      const matches = [];
      const patterns = [
        /knowledge-asset:\/\/\d+(?![A-Za-z0-9])/gi,
        /https?:\/\/(?:zskcdn\.yjllq\.com|zskdb\.yjllq\.com)\/[^\s\)\]\>]+/gi
      ];
      patterns.forEach(pattern => { let match; while ((match = pattern.exec(source))) matches.push(match[0]); });
      return [...new Set(matches)];
    },
    normalizeAssetUrls(content) {
      const source = String(content || '');
      return source.replace(/https?:\/\/(?:zskcdn\.yjllq\.com|zskdb\.yjllq\.com)\/[^\s\)\]\>]+/gi, url => {
        let base = url;
        try { const parsed = new URL(url); base = `${parsed.origin}${decodeURIComponent(parsed.pathname)}`; } catch (e) { base = url.split('?')[0]; }
        const asset = this.assets.find(item => {
          if (!item || !item.id) return false;
          const key = String(item.qiniu_key || '');
          if (!key) return false;
          return base.endsWith('/' + key) || base.endsWith('/' + decodeURIComponent(key));
        });
        return asset ? (asset.reference || `knowledge-asset://${asset.id}`) : url;
      });
    },
    scheduleAssetPreview() {
      if (this.assetResolveTimer) clearTimeout(this.assetResolveTimer);
      this.assetResolveTimer = setTimeout(() => this.refreshAssetPreview(), 120);
    },
    async refreshAssetPreview() {
      const tab = this.activeTab;
      const content = tab ? String(tab.content || '') : '';
      const requestId = ++this.previewRequestId;
      const references = this.assetReferencesInContent(content);
      const now = Math.floor(Date.now() / 1000);
      const stale = references.filter(reference => !this.assetUrlCache[reference] || Number(this.assetUrlCache[reference].expires_at || 0) <= now + 30);
      if (stale.length) {
        try {
          const res = await resolveKnowledgeAssets(this.ck, stale);
          if (res.code === 0 || res.code === 200) {
            (res.data?.items || []).forEach(item => {
              const entry = { ...item, expires_at: Number(item.expires_at || (now + Number(item.expires_in || 900))) };
              this.$set(this.assetUrlCache, item.requested_reference || item.reference, entry);
              this.$set(this.assetUrlCache, item.reference, entry);
            });
            (res.data?.missing || []).forEach(reference => this.$set(this.assetUrlCache, reference, { missing: true, expires_at: now + 60 }));
          }
        } catch (error) {
          if (requestId === this.previewRequestId) this.notify(error.message || '附件访问地址解析失败');
        }
      }
      if (requestId !== this.previewRequestId) return;
      let rendered = content;
      references.forEach(reference => {
        const entry = this.assetUrlCache[reference];
        rendered = rendered.split(reference).join(entry && entry.url ? entry.url : (entry && entry.missing ? '#' : reference));
      });
      this.previewHtml = md.render(this.replaceWikiLinks(rendered));
      if (this.assetRefreshTimer) clearTimeout(this.assetRefreshTimer);
      const expiries = references.map(reference => Number(this.assetUrlCache[reference]?.expires_at || 0)).filter(Boolean);
      if (expiries.length) {
        const next = Math.max(10000, (Math.min(...expiries) - Math.floor(Date.now() / 1000) - 60) * 1000);
        this.assetRefreshTimer = setTimeout(() => this.refreshAssetPreview(), next);
      }
    },
    handlePreviewAssetError(event) {
      const target = event && event.target;
      const url = target && (target.currentSrc || target.src);
      if (!url || !/^https?:\/\//i.test(url)) return;
      const now = Date.now();
      if (now - this.assetErrorRetryAt < 3000) return;
      this.assetErrorRetryAt = now;
      Object.keys(this.assetUrlCache).forEach(reference => {
        if (this.assetUrlCache[reference] && this.assetUrlCache[reference].url === url) this.$delete(this.assetUrlCache, reference);
      });
      this.refreshAssetPreview();
    },
    renderMarkdown(content) { return md.render(this.replaceWikiLinks(content || '')); },
    replaceWikiLinks(content) { return content.replace(/(!)?\[\[([^\]|#]+)(?:#[^\]|]+)?(?:\|([^\]]+))?\]\]/g, (all, embed, target, label) => embed ? `![${label || target}](${target})` : `[${label || target}](knowledge://${encodeURIComponent(target)})`); },
    handlePreviewClick(event) { const link = event.target.closest && event.target.closest('a[href^="knowledge://"]'); if (!link) return; event.preventDefault(); const target = decodeURIComponent(link.getAttribute('href').slice('knowledge://'.length)); const doc = this.documents.find(item => item.title === target || item.path === target || item.slug === target || item.slug === `${target}.md`); if (doc) this.openDocument(doc.id); },
    jumpToHeading(heading) {
      const component = this.$refs.editor;
      const editor = component && component.$refs && component.$refs.vNoteTextarea && component.$refs.vNoteTextarea.$el
        ? component.$refs.vNoteTextarea.$el.querySelector('textarea')
        : null;
      if (!editor || !this.activeTab) return;
      const lines = this.activeTab.content.split(/\r?\n/);
      const offset = lines.slice(0, heading.line).join('\n').length + (heading.line ? 1 : 0);
      editor.focus();
      editor.setSelectionRange(offset, offset);
      editor.scrollTop = Math.max(0, (heading.line * 24) - (editor.clientHeight / 3));
    },
    async runSearch() { const query = this.searchText.trim(); if (!query) return; this.isSearching = true; try { const res = await searchKnowledgeWorkspace(this.ck, query); this.searchResults = res.data?.items || []; } finally { this.isSearching = false; } },
    debouncedSearch() { if (this.searchTimer) clearTimeout(this.searchTimer); if (!this.searchText.trim()) { this.searchResults = []; return; } this.searchTimer = setTimeout(() => this.runSearch(), 300); },
    clearSearch() { this.searchText = ''; this.searchResults = []; },
    toggleCommandPalette() { this.commandPaletteOpen = !this.commandPaletteOpen; this.$nextTick(() => this.$refs.commandInput && this.$refs.commandInput.focus()); },
    runCommand() { const command = this.commandText.trim(); this.commandPaletteOpen = false; this.commandText = ''; if (/搜索/.test(command)) this.runSearch(); else if (/目录/.test(command)) this.createFolder(); else this.newDocument(); },
    handleKeydown(event) { if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'p') { event.preventDefault(); this.toggleCommandPalette(); } if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') { event.preventDefault(); this.saveDocument(); } },
    async handleEditorImageAdd(pos, file) {
      if (!file || !this.activeTab) return;
      this.pendingImageUploads += 1;
      try {
        const res = await uploadKnowledgeAsset(this.ck, file);
        if (res.code === 0 || res.code === 200) {
          const asset = res.data || {};
          const component = this.$refs.editor;
          const reference = /^knowledge-asset:\/\/\d+$/i.test(String(asset.reference || '')) ? asset.reference : `knowledge-asset://${asset.id}`;
          if (component && typeof component.$img2Url === 'function' && asset.id) {
            component.$img2Url(pos, reference);
          }
          this.addUploadedAsset(asset);
          this.markDirty();
        } else {
          this.removeEditorImagePlaceholder(pos, file);
          this.notify(res.message || '图片上传失败');
        }
      } catch (error) {
        this.removeEditorImagePlaceholder(pos, file);
        this.notify(error.message || '图片上传失败');
      } finally {
        this.pendingImageUploads = Math.max(0, this.pendingImageUploads - 1);
      }
    },
    removeEditorImagePlaceholder(pos, file) {
      if (!this.activeTab) return;
      const name = String(file._name || file.name || '').replace(/[\[\]\(\)\+\{\}&\|\\\*\^%$#@\-]/g, '');
      const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      this.activeTab.content = this.activeTab.content.replace(new RegExp(`!\\[${escapedName}\\]\\(${String(pos)}\\)`, 'g'), '');
      this.markDirty();
    },
    async dropAsset(event) { const file = event.dataTransfer.files && event.dataTransfer.files[0]; if (!file || !this.activeTab || file.type.startsWith('image/')) return; const res = await uploadKnowledgeAsset(this.ck, file); if (res.code === 0 || res.code === 200) { const asset = res.data; this.addUploadedAsset(asset); this.insertAsset(asset); } else this.notify(res.message || '附件上传失败'); },
    async handleAssetPicker(event) { const files = Array.from(event.target.files || []); for (const file of files) { try { const res = await uploadKnowledgeAsset(this.ck, file); if (res.code === 0 || res.code === 200) { this.addUploadedAsset(res.data); } else this.notify(`${file.name}：${res.message || '附件上传失败'}`); } catch (error) { this.notify(`${file.name}：${error.message || '附件上传失败'}`); } } if (event.target) event.target.value = ''; },
    addUploadedAsset(asset) { if (!asset || !asset.id) return; const index = this.assets.findIndex(item => Number(item.id) === Number(asset.id)); if (index >= 0) this.$set(this.assets, index, { ...this.assets[index], ...asset }); else this.assets.unshift(asset); if (asset.used_bytes !== undefined) this.assetUsage = { ...this.assetUsage, used_bytes: Number(asset.used_bytes), quota_bytes: Number(asset.quota_bytes || this.assetUsage.quota_bytes), remaining_bytes: Number(asset.remaining_bytes || 0), used_percent: Number(asset.used_percent || 0) }; },
    async deleteAsset(asset) { if (!asset || !await showConfirm(`删除附件“${asset.original_name || '未命名'}”？`, { dangerous: true, confirmText: '删除' })) return; try { const res = await deleteKnowledgeAsset(this.ck, asset.id); if (res.code === 0 || res.code === 200) { this.assets = this.assets.filter(item => Number(item.id) !== Number(asset.id)); if (res.data && res.data.usage) this.assetUsage = res.data.usage; else await this.bootstrap(); } else this.notify(res.message || '附件删除失败'); } catch (error) { this.notify(error.message || '附件删除失败'); } },
    insertAsset(asset) { if (!asset || !asset.id) return; if (!this.activeTab) { this.notify('请先打开一篇文章'); return; } const name = String(asset.original_name || '附件').replace(/[\[\]\\]/g, ''); const reference = /^knowledge-asset:\/\/\d+$/i.test(String(asset.reference || '')) ? asset.reference : `knowledge-asset://${asset.id}`; const isImage = /^image\//i.test(asset.mime_type || ''); const markdown = isImage ? `![${name}](${reference})` : `[${name}](${reference})`; this.activeTab.content += `\n${markdown}\n`; this.markDirty(); },
    applyPreset() { if (!this.creationPreset || !this.creationPresetKey) return; this.newDocument(); if (this.activeTab) { this.activeTab.title = this.creationPreset.title || '未命名文章'; this.activeTab.content = this.creationPreset.content || ''; this.activeTab.dirty = true; } },
    openDocumentImport() { if (!this.importingDocuments && this.$refs.documentImportInput) this.$refs.documentImportInput.click(); },
    async handleDocumentImportPicker(event) {
      const files = Array.from((event && event.target && event.target.files) || []);
      if (event && event.target) event.target.value = '';
      if (files.length) await this.importDocuments(files);
    },
    importDocumentTitle(file) {
      const name = String(file && file.name || '').replace(/\\/g, '/').split('/').pop() || '未命名文章';
      return name.replace(/\.[^.]+$/, '') || '未命名文章';
    },
    async importDocuments(files) {
      if (this.importingDocuments || !files || !files.length) return;
      this.importingDocuments = true;
      this.importProgress = `0/${files.length}`;
      const successes = [];
      const failures = [];
      const folderId = Number(this.selectedFolderId || 0);
      try {
        for (let index = 0; index < files.length; index += 1) {
          const file = files[index];
          const filename = file && file.name ? file.name : `文件${index + 1}`;
          this.importProgress = `${index + 1}/${files.length}`;
          try {
            const converted = await convertKnowledgeFileToMarkdown(file);
            const response = await createKnowledgeDocument(this.ck, {
              title: this.importDocumentTitle(file),
              content: converted.markdown,
              folder_id: folderId
            });
            if (response.code !== 0 && response.code !== 200) throw new Error(response.message || '文章创建失败');
            if (response.data && response.data.id) successes.push(response.data);
            else successes.push({ title: this.importDocumentTitle(file) });
          } catch (error) {
            failures.push(`${filename}：${error && error.message ? error.message : '导入失败'}`);
          }
        }
        if (successes.length) {
          await this.bootstrap();
          const first = successes.find(item => item && item.id);
          if (first) await this.openDocument(first.id);
        }
        if (failures.length) {
          const summary = `已导入 ${successes.length} 个文件，${failures.length} 个失败：${failures.join('；')}`;
          this.notify(summary);
        } else {
          this.notify(`已成功导入 ${successes.length} 个文件`);
        }
      } finally {
        this.importingDocuments = false;
        this.importProgress = '';
      }
    },
    async handleHostUpload(event) {
      const files = event && event.detail && event.detail.files
        ? Array.from(event.detail.files)
        : [event && event.detail && event.detail.file ? event.detail.file : (typeof File !== 'undefined' && event instanceof File ? event : null)];
      await this.importDocuments(files.filter(Boolean));
    },
    beforeUnload(event) { this.persistWorkspaceState(true); if (this.tabs.some(tab => tab.dirty)) { event.preventDefault(); event.returnValue = ''; } },
    formatTime(value) { if (!value) return ''; const date = new Date(value); return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); },
    formatBytes(value) { const bytes = Number(value) || 0; if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`; if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`; return `${bytes} B`; },
    assetKind(asset) { if (/^image\//i.test(asset.mime_type || '')) return '图'; if (/pdf/i.test(asset.mime_type || '') || /\.pdf$/i.test(asset.original_name || '')) return 'PDF'; return '件'; },
    notify(message) { if (this.$emit) this.$emit('notify', message); }
  }
};
</script>

<style scoped>
.knowledge-workspace{height:100%;display:flex;flex-direction:column;background:var(--bg-card,#fff);color:var(--text-primary,#1f2937);min-height:0}.document-import-input{display:none}.document-import-action:disabled{cursor:wait;opacity:.6}.workspace-toolbar{height:52px;display:flex;align-items:center;gap:18px;padding:0 18px;border-bottom:1px solid var(--border-color,#e5e7eb);flex-shrink:0}.workspace-brand{display:flex;align-items:center;gap:8px;min-width:140px}.brand-mark{color:#7c3aed}.workspace-search{flex:1;max-width:620px;position:relative}.workspace-search input{width:100%;padding:9px 90px 9px 12px;border:1px solid var(--border-color,#e5e7eb);border-radius:8px;background:var(--bg-primary,#f9fafb);color:inherit}.search-hint{position:absolute;right:10px;top:9px;color:#9ca3af;font-size:12px}.workspace-actions{display:flex;gap:8px;margin-left:auto}.workspace-actions button,.editor-toolbar button,.browser-heading button,.sidebar-toolbar button,.panel-title button{border:0;background:transparent;color:inherit;cursor:pointer}.workspace-actions button{padding:7px 10px;border-radius:6px}.workspace-actions button:hover{background:#f3f4f6}.workspace-body{display:grid;grid-template-columns:240px minmax(0,1fr) 260px;min-height:0;flex:1}.vault-sidebar,.context-sidebar{overflow:auto;border-right:1px solid var(--border-color,#e5e7eb);padding:14px}.context-sidebar{border-right:0;border-left:1px solid var(--border-color,#e5e7eb)}.sidebar-toolbar,.panel-title,.browser-heading,.editor-toolbar,.tab-bar{display:flex;align-items:center;justify-content:space-between}.sidebar-toolbar{font-size:12px;color:#6b7280;text-transform:uppercase;margin-bottom:12px}.sidebar-toolbar button{font-size:20px}.sidebar-section{display:flex;flex-direction:column;gap:2px}.nav-item,.tree-row,.outline-row,.backlink-row,.revision-row{display:block;width:100%;text-align:left;border:0;background:transparent;color:inherit;padding:7px 8px;border-radius:6px;cursor:pointer}.nav-item:hover,.tree-row:hover,.outline-row:hover,.backlink-row:hover,.revision-row:hover,.nav-item.active,.tree-row.active{background:#f3f4f6}.nav-item small{float:right;color:#9ca3af}.sidebar-heading{margin:18px 8px 6px;color:#9ca3af;font-size:11px;text-transform:uppercase}.folder-tree,.tag-list{display:flex;flex-direction:column;gap:2px}.tree-row{font-size:13px}.tree-row span{margin-right:7px;color:#9ca3af}.tag-list{flex-direction:row;flex-wrap:wrap}.tag-chip{border:0;background:#f3f4f6;border-radius:999px;padding:5px 8px;color:#4b5563;cursor:pointer}.tag-chip.active{background:#ede9fe;color:#6d28d9}.tag-chip small{color:#9ca3af}.workspace-main{min-width:0;min-height:0;display:flex;flex-direction:column}.mobile-toolbar{display:none}.document-browser,.editor-tabs{min-height:0;flex:1;display:flex;flex-direction:column;padding:22px}.browser-heading{margin-bottom:18px}.browser-heading h2{font-size:20px;margin:0}.document-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:12px;overflow:auto}.document-card{position:relative;text-align:left;border:1px solid var(--border-color,#e5e7eb);background:transparent;color:inherit;border-radius:10px;padding:15px;min-height:118px;display:flex;flex-direction:column;gap:7px;cursor:pointer}.document-card:hover{border-color:#8b5cf6;box-shadow:0 5px 18px rgba(79,70,229,.1)}.doc-card-icon{color:#8b5cf6}.document-card small,.document-card em{color:#9ca3af;font-size:12px;font-style:normal;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.document-card i{position:absolute;right:10px;top:10px;color:#f59e0b;font-style:normal}.tab-bar{justify-content:flex-start;gap:2px;border-bottom:1px solid var(--border-color,#e5e7eb);overflow:auto}.editor-tab{border:0;background:transparent;color:#6b7280;padding:9px 12px;cursor:pointer;white-space:nowrap}.editor-tab.active{color:inherit;border-bottom:2px solid #7c3aed}.editor-tab.dirty{font-style:italic}.editor-tab b{margin-left:8px;font-weight:normal}.new-tab{border:0;background:transparent;font-size:18px;cursor:pointer}.editor-toolbar{gap:10px;padding:10px 0}.editor-toolbar input{flex:1;border:0;background:transparent;color:inherit;font-size:16px;font-weight:600;outline:0}.save-state{font-size:12px;color:#9ca3af}.editor-split{display:grid;grid-template-columns:1fr 1fr;gap:1px;min-height:0;flex:1;border:1px solid var(--border-color,#e5e7eb);border-radius:8px;overflow:hidden}.editor-split textarea{resize:none;border:0;padding:18px;font:14px/1.7 ui-monospace,SFMono-Regular,Menlo,monospace;background:#fafafa;color:#1f2937;outline:0}.preview{overflow:auto;padding:18px;background:var(--bg-card,#fff)}.context-block{padding-bottom:18px;margin-bottom:18px;border-bottom:1px solid var(--border-color,#e5e7eb)}.panel-title{font-weight:600;font-size:13px;margin-bottom:8px}.panel-title small{color:#9ca3af}.property-row{display:flex;justify-content:space-between;gap:8px;padding:5px 0;font-size:12px}.property-row code{color:#6d28d9;overflow:hidden;text-overflow:ellipsis}.empty-text,.context-empty{color:#9ca3af;font-size:13px;padding:12px 0}.search-results{padding:20px;overflow:auto}.search-result{display:flex;flex-direction:column;gap:4px;text-align:left;width:100%;border:0;border-bottom:1px solid var(--border-color,#e5e7eb);background:transparent;color:inherit;padding:13px 0;cursor:pointer}.search-result small{color:#9ca3af}.search-result span{font-size:13px;color:#6b7280}.command-overlay{position:fixed;inset:0;z-index:1200;background:rgba(17,24,39,.35);display:flex;justify-content:center;padding-top:14vh}.command-palette{width:min(560px,calc(100vw - 30px));background:#fff;border-radius:12px;box-shadow:0 20px 60px rgba(0,0,0,.25);padding:10px;display:flex;flex-direction:column;gap:5px}.command-palette input{padding:12px;border:0;border-bottom:1px solid #e5e7eb;outline:0}.command-palette button{text-align:left;padding:10px;border:0;background:transparent;cursor:pointer}.command-palette button:hover{background:#f3f4f6}.search-results :deep(mark),.preview :deep(mark){background:#fef08a}.preview :deep(.markdown-body){max-width:none}@media(max-width:900px){.workspace-body{grid-template-columns:200px minmax(0,1fr)}.context-sidebar{display:none}.context-sidebar.mobile-open{display:block;position:fixed;right:0;top:52px;bottom:0;width:280px;background:#fff;z-index:10;box-shadow:-8px 0 24px rgba(0,0,0,.12)}.workspace-toolbar{gap:8px}.workspace-brand{min-width:auto}.workspace-search{max-width:none}.workspace-actions button:first-child{display:none}}@media(max-width:640px){.workspace-toolbar{height:48px;padding:0 10px}.workspace-brand strong,.search-hint{display:none}.workspace-body{display:block}.vault-sidebar{display:none}.vault-sidebar.mobile-open{display:block;position:fixed;left:0;top:48px;bottom:0;width:260px;background:#fff;z-index:10;box-shadow:8px 0 24px rgba(0,0,0,.12)}.mobile-toolbar{display:flex;justify-content:space-between;align-items:center;padding:8px 12px;border-bottom:1px solid var(--border-color,#e5e7eb)}.document-browser,.editor-tabs{padding:12px}.editor-split{grid-template-columns:1fr}.preview{display:none}.document-grid{grid-template-columns:1fr}.workspace-actions button{padding:5px}.workspace-search input{padding:7px 10px}}
</style>

<style scoped>
.asset-input { display: none; }
.assets-block { padding-bottom: 12px; }
.asset-quota { display: flex; justify-content: space-between; margin: 8px 0 5px; color: var(--kb-muted, #8997ab); font-size: 11px; }
.quota-track { height: 5px; margin-bottom: 9px; overflow: hidden; border-radius: 999px; background: var(--kb-primary-soft, #e7f1ff); }
.quota-track i { display: block; height: 100%; max-width: 100%; border-radius: inherit; background: var(--kb-primary, #1677ff); transition: width .2s ease; }
.asset-row { display: flex; align-items: center; gap: 7px; width: 100%; min-width: 0; padding: 7px 5px; border: 0; border-radius: 7px; background: transparent; color: var(--kb-text, #27364f); text-align: left; cursor: pointer; }
.asset-row:hover { background: var(--kb-primary-soft, #e7f1ff); }
.asset-kind { display: inline-grid; flex: 0 0 27px; height: 24px; place-items: center; border-radius: 6px; background: var(--kb-primary-soft, #e7f1ff); color: var(--kb-primary, #1677ff); font-size: 9px; font-weight: 700; }
.asset-name { min-width: 0; overflow: hidden; flex: 1; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; }
.asset-row small { flex: 0 0 auto; color: var(--kb-muted, #8997ab); font-size: 10px; }
.asset-remove { flex: 0 0 auto; padding: 1px 3px; border-radius: 4px; color: #a9b5c5; font-size: 14px; font-weight: 500; line-height: 1; }
.asset-remove:hover { background: #fff0f0; color: #d9363e; }
[data-theme="dark"] .asset-row { color: #dbeafe; }
</style>

<style scoped>
/* Shared rail controls: both desktop and mobile can hide either side independently. */
.workspace-body {
  grid-template-columns: 294px minmax(0, 1fr) 280px;
  transition: grid-template-columns .2s ease;
}
.workspace-body.vault-collapsed { grid-template-columns: 0 minmax(0, 1fr) 280px; }
.workspace-body.context-collapsed { grid-template-columns: 294px minmax(0, 1fr) 0; }
.workspace-body.vault-collapsed.context-collapsed { grid-template-columns: 0 minmax(0, 1fr) 0; }
.vault-sidebar, .context-sidebar {
  min-width: 0;
  transition: opacity .16s ease, padding .2s ease, border-width .2s ease;
}
.vault-sidebar.is-collapsed, .context-sidebar.is-collapsed {
  overflow: hidden;
  padding-left: 0;
  padding-right: 0;
  border-width: 0;
  opacity: 0;
  pointer-events: none;
}
.sidebar-toolbar-actions { display: flex; align-items: center; gap: 5px; }
.sidebar-toolbar-actions button { display: inline-grid; place-items: center; }
.mobile-toolbar-leading { display: inline-flex; align-items: center; gap: 6px; }
.mobile-close-button { color: #d9363e !important; font-size: 23px; line-height: 1; }
.context-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 35px;
  margin: -8px 0 18px;
  color: var(--kb-muted, #8997ab);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .04em;
}
.context-toolbar button {
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  background: var(--kb-primary-soft, #e7f1ff);
  color: var(--kb-primary, #1677ff);
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
}
.context-toolbar button:hover { background: var(--kb-primary, #1677ff); color: #fff; }
.rail-toggle { min-width: 32px; padding: 8px 7px !important; color: var(--kb-muted, #8997ab) !important; }
.rail-toggle:hover, .rail-toggle.active:hover { color: var(--kb-primary, #1677ff) !important; }

@media (max-width: 900px) {
  .workspace-body { grid-template-columns: 212px minmax(0, 1fr); }
  .workspace-body.vault-collapsed { grid-template-columns: 0 minmax(0, 1fr); }
  .workspace-body.context-collapsed { grid-template-columns: 212px minmax(0, 1fr); }
  .workspace-body.vault-collapsed.context-collapsed { grid-template-columns: 0 minmax(0, 1fr); }
  .context-toolbar { margin-top: -2px; }
}

@media (max-width: 640px) {
  .workspace-body,
  .workspace-body.vault-collapsed,
  .workspace-body.context-collapsed,
  .workspace-body.vault-collapsed.context-collapsed { display: block; }
  .workspace-toolbar { gap: 7px; }
  .workspace-brand { flex: 0 0 auto; }
  .workspace-search { min-width: 0; }
  .workspace-actions { gap: 2px; }
  .workspace-actions .rail-toggle { display: none; }
  .vault-sidebar.is-collapsed, .context-sidebar.is-collapsed { display: none; }
  .vault-sidebar.mobile-open {
    display: block;
    width: min(86vw, 310px);
    padding: 18px 14px;
    border-right: 1px solid #e0e9f5;
    opacity: 1;
    pointer-events: auto;
  }
  .context-sidebar.mobile-open {
    display: block;
    width: min(88vw, 330px);
    padding: 20px 16px;
    border-left: 1px solid #e3eaf3;
    opacity: 1;
    pointer-events: auto;
  }
  .mobile-toolbar { position: sticky; top: 0; z-index: 4; min-height: 46px; }
  .mobile-toolbar button {
    width: 36px;
    height: 34px;
    border: 1px solid var(--kb-border, #e4ebf5);
    border-radius: 9px;
    background: var(--kb-surface, #fff);
    color: var(--kb-primary, #1677ff);
    cursor: pointer;
  }
  .mobile-toolbar span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; font-weight: 600; }
  .document-browser, .editor-tabs { padding: 12px; }
  .browser-heading { gap: 8px; align-items: flex-start; }
  .browser-heading h2 { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 19px; }
  .browser-heading button { flex: 0 0 auto; padding: 8px 10px; }
  .document-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 9px; }
  .document-card { min-height: 112px; padding: 13px; border-radius: 11px; }
  .editor-toolbar { flex-wrap: wrap; gap: 8px; padding: 11px 0; }
  .editor-toolbar input { flex: 1 1 100%; min-width: 0; font-size: 17px; }
  .folder-select { max-width: 145px; flex: 1 1 120px; }
  .editor-split { min-height: 0; }
  .markdown-editor-host { min-height: 48vh; }
  .preview { display: none; }
}

@media (max-width: 390px) {
  .workspace-actions button { padding-left: 6px; padding-right: 6px; }
  .document-grid { grid-template-columns: 1fr; }
  .workspace-search input { padding-left: 30px; }
  .workspace-search::before { left: 10px; }
}
</style>

<style scoped>
.markdown-editor-host.is-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 3000;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
  background: #fbfcfe;
}
.markdown-editor-host.is-fullscreen >>> .markdown-editor,
.markdown-editor-host.is-fullscreen >>> .v-note-wrapper {
  width: 100%;
  height: 100%;
  min-height: 0;
}
[data-theme="dark"] .markdown-editor-host.is-fullscreen,
[data-theme="dark"] .markdown-editor-host.is-fullscreen >>> .v-note-wrapper {
  background: #121c2c;
}
</style>

<style scoped>
.browser-heading-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
}
.document-sort-label {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--kb-muted, #8997ab);
  font-size: 12px;
  white-space: nowrap;
}
.document-sort-select {
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--kb-border, #e1e8f2);
  border-radius: 9px;
  outline: none;
  background: var(--bg-card, #fff);
  color: var(--text-primary, #27364f);
  font-size: 12px;
  cursor: pointer;
}
.document-sort-select:focus { border-color: var(--kb-primary, #1677ff); }
[data-theme="dark"] .document-sort-select {
  border-color: var(--kb-border, #2a3a52);
  background: var(--kb-input, #141e2e);
  color: var(--kb-text, #edf4ff);
}
@media (max-width: 640px) {
  .browser-heading { flex-wrap: wrap; }
  .browser-heading-actions { width: 100%; justify-content: space-between; }
}
</style>

<style scoped>
.editor-more-actions { position: relative; flex: 0 0 auto; }
.editor-toolbar .editor-more-trigger {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  padding: 0;
  border: 1px solid var(--kb-border, #e1e8f2);
  border-radius: 10px;
  background: #fff;
  color: #718198;
}
.editor-toolbar .editor-more-trigger:hover,
.editor-toolbar .editor-more-trigger[aria-expanded="true"] {
  border-color: #cce1ff;
  background: #eaf3ff;
  color: var(--kb-primary, #1677ff);
}
.editor-more-menu {
  position: absolute;
  z-index: 50;
  top: calc(100% + 7px);
  right: 0;
  width: 190px;
  padding: 5px;
  border: 1px solid #dce7f4;
  border-radius: 11px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(45,80,130,.16);
}
.editor-toolbar .editor-more-menu button {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  min-height: 38px;
  padding: 8px 10px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #51627a;
  text-align: left;
  font-size: 13px;
  font-weight: 400;
}
.editor-toolbar .editor-more-menu button:hover:not(:disabled) { background: #eaf3ff; color: #1677ff; }
.editor-toolbar .editor-more-menu button:disabled { opacity: .45; cursor: not-allowed; }
.editor-toolbar .editor-more-menu button.danger { color: #d9363e; }
.editor-toolbar .editor-more-menu button.danger:hover:not(:disabled) { background: #fff0f0; color: #c62828; }
.more-menu-favorite { width: 16px; color: #e3a600; text-align: center; font-size: 16px; }
[data-theme="dark"] .editor-toolbar .editor-more-trigger { border-color: #30445f; background: #182235; color: #a8bad3; }
[data-theme="dark"] .editor-toolbar .editor-more-trigger:hover,
[data-theme="dark"] .editor-toolbar .editor-more-trigger[aria-expanded="true"] { background: #223b5d; color: #8fc0ff; }
[data-theme="dark"] .editor-more-menu { border-color: #30445f; background: #1b2940; box-shadow: 0 12px 28px rgba(0,0,0,.35); }
[data-theme="dark"] .editor-toolbar .editor-more-menu button { color: #dbeafe; }
[data-theme="dark"] .editor-toolbar .editor-more-menu button:hover:not(:disabled) { background: #223b5d; color: #8fc0ff; }
[data-theme="dark"] .editor-toolbar .editor-more-menu button.danger { color: #ff8b8b; }
</style>

<style scoped>
.editor-toolbar {
  min-width: 0;
  flex-wrap: wrap;
}
.editor-title-fields {
  display: flex;
  flex: 1 1 360px;
  align-items: center;
  gap: 10px;
  min-width: 300px;
}
.editor-title-fields input {
  flex: 1 1 180px;
  min-width: 0;
}
.editor-toolbar-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 9px;
  margin-left: auto;
}
.editor-toolbar-actions .save-state {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (max-width: 640px) {
  .editor-title-fields {
    flex: 1 1 100%;
    flex-wrap: wrap;
    min-width: 0;
  }
  .editor-title-fields input { flex: 1 1 100%; }
  .editor-toolbar-actions { width: 100%; justify-content: flex-end; }
}
</style>

<style scoped>
.editor-split {
  grid-template-columns: minmax(0, var(--editor-left-width, 50%)) 10px minmax(0, 1fr);
  gap: 0;
}
.editor-split-handle {
  position: relative;
  z-index: 1;
  width: 10px;
  min-width: 10px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: col-resize;
  touch-action: none;
}
.editor-split-handle::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 4px;
  width: 1px;
  background: var(--kb-border, #e2e9f2);
  content: '';
  transition: background .15s ease, width .15s ease, left .15s ease;
}
.editor-split-handle:hover::before,
.editor-split-handle:focus-visible::before,
.editor-split.is-resizing .editor-split-handle::before {
  left: 3px;
  width: 3px;
  background: var(--kb-primary, #1677ff);
}
.editor-split-handle:focus-visible { outline: none; }
.editor-split.is-resizing,
.editor-split.is-resizing * { user-select: none; }
@media (max-width: 640px) {
  .editor-split { grid-template-columns: minmax(0, 1fr) !important; }
  .editor-split-handle { display: none; }
}
</style>

<style scoped>
/* mavon-editor is used as the Markdown source editor. The workspace keeps its
   own preview pane so wiki-link navigation and CDN asset rendering continue
   to use the application's existing behavior. */
.markdown-editor-host {
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  background: #fbfcfe;
}
.markdown-editor-host >>> .markdown-editor,
.markdown-editor-host >>> .v-note-wrapper {
  height: 100%;
  min-height: 0;
  border: 0;
  border-radius: 0;
  box-shadow: none !important;
}
.markdown-editor-host >>> .v-note-wrapper {
  /* mavon-editor's high default z-index can cover the workspace controls. */
  position: relative;
  z-index: 0;
}
.markdown-editor-host >>> .v-note-op {
  min-height: 42px;
  padding: 5px 8px;
  background: #f5f8fc !important;
  border-bottom: 1px solid #e2e9f2;
}
.markdown-editor-host >>> .v-note-op button,
.markdown-editor-host >>> .v-note-op .op-icon {
  color: #6f8199;
}
.markdown-editor-host >>> .v-note-op button:hover,
.markdown-editor-host >>> .v-note-op .op-icon:hover {
  color: #1677ff;
  background: #e7f1ff;
}
.markdown-editor-host >>> .v-note-panel {
  height: calc(100% - 42px);
  min-height: 0;
}
.markdown-editor-host >>> .v-note-edit {
  height: 100%;
  min-height: 0;
  background: #fbfcfe;
}
.markdown-editor-host >>> .content-input-wrapper,
.markdown-editor-host >>> .content-input,
.markdown-editor-host >>> .content-input textarea {
  height: 100%;
  min-height: 0;
  background: #fbfcfe !important;
  color: #35445d;
  font: 14px/1.8 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}
.markdown-editor-host >>> .content-input textarea {
  box-sizing: border-box;
  padding: 22px 24px;
  outline: none;
}
.markdown-editor-host >>> .v-note-show {
  display: none !important;
}
[data-theme="dark"] .markdown-editor-host,
[data-theme="dark"] .markdown-editor-host >>> .v-note-edit,
[data-theme="dark"] .markdown-editor-host >>> .content-input-wrapper,
[data-theme="dark"] .markdown-editor-host >>> .content-input,
[data-theme="dark"] .markdown-editor-host >>> .content-input textarea {
  background: #121c2c !important;
  color: #dbeafe;
}
[data-theme="dark"] .markdown-editor-host >>> .v-note-op {
  background: #182235 !important;
  border-bottom-color: #2a3a52;
}
[data-theme="dark"] .markdown-editor-host >>> .v-note-op button,
[data-theme="dark"] .markdown-editor-host >>> .v-note-op .op-icon {
  color: #a8bad3;
}
@media (max-width: 640px) {
  .markdown-editor-host >>> .v-note-op { min-height: 38px; }
  .markdown-editor-host >>> .v-note-panel { height: calc(100% - 38px); }
  .markdown-editor-host >>> .content-input textarea { padding: 16px; }
}
</style>

<style scoped>
/* Main shell alignment: the knowledge workspace uses the same blue/grey
   language as the application's navigation rail. */
.knowledge-workspace {
  --kb-bg: #f7f9fd;
  --kb-surface: #ffffff;
  --kb-input: #ffffff;
  --kb-text: #27364f;
  --kb-muted: #8997ab;
  --kb-border: #e4ebf5;
  --kb-primary: #1677ff;
  --kb-primary-soft: #e7f1ff;
  background: var(--kb-bg);
  color: var(--kb-text);
}
.workspace-toolbar {
  height: 64px;
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid var(--kb-border);
  box-shadow: 0 1px 0 rgba(27, 75, 135, .02);
}
.workspace-brand { min-width: 192px; color: #17243b; font-size: 16px; }
.brand-mark { width: 32px; height: 32px; border-radius: 11px; color: var(--kb-primary); background: var(--kb-primary-soft); box-shadow: none; }
.workspace-search { max-width: 845px; }
.workspace-search input {
  height: 42px; padding: 0 92px 0 42px; border: 1px solid #e1e8f2;
  border-radius: 13px; background: #fbfcfe; color: var(--kb-text);
  box-shadow: inset 0 1px 2px rgba(41, 75, 125, .02);
}
.workspace-search::before { left: 15px; top: 8px; color: #9aa7b8; font-size: 21px; }
.workspace-search input::placeholder { color: #9aa7b8; }
.workspace-search input:focus { background: #fff; border-color: #9fc5ff; box-shadow: 0 0 0 3px rgba(22,119,255,.1); }
.search-hint { top: 13px; color: #a0aabd; }
.workspace-actions button { border-radius: 11px; padding: 10px 15px; color: #8694a8; font-weight: 500; }
.workspace-actions button:first-child { background: var(--kb-primary); box-shadow: 0 7px 14px rgba(22,119,255,.2); color: #fff; }
.workspace-actions button:hover { background: var(--kb-primary-soft); color: var(--kb-primary); transform: none; }
.workspace-actions button:first-child:hover { background: #0d6ceb; color: #fff; }
.workspace-body { grid-template-columns: 294px minmax(0, 1fr) 280px; background: var(--kb-bg); }
.vault-sidebar {
  padding: 28px 17px; background: #f4f8fe; border-right: 1px solid #e0e9f5;
}
.context-sidebar { padding: 30px 18px; background: #fff; border-left: 1px solid #e3eaf3; }
.sidebar-toolbar { margin: 0 7px 18px; color: #7f8da3; font-size: 12px; letter-spacing: .06em; }
.sidebar-toolbar button { width: 35px; height: 35px; border-radius: 10px; background: #e1efff; color: var(--kb-primary); font-size: 23px; }
.sidebar-toolbar button:hover { background: var(--kb-primary); color: #fff; }
.sidebar-section { gap: 6px; }
.nav-item, .tree-row, .outline-row, .backlink-row, .revision-row {
  min-height: 42px; padding: 10px 12px; border-radius: 11px; color: #718198; font-size: 14px;
}
.nav-item:hover, .tree-row:hover, .outline-row:hover, .backlink-row:hover, .revision-row:hover,
.nav-item.active, .tree-row.active { background: #e2efff; color: var(--kb-primary); }
.nav-item.active { font-weight: 600; }
.nav-item small { color: #8797ad; }
.sidebar-heading { margin: 25px 9px 9px; color: #8593a7; font-size: 12px; font-weight: 600; letter-spacing: .04em; }
.tree-row { min-height: 38px; }
.tree-row span { color: var(--kb-primary); }
.tag-list { gap: 7px; }
.tag-chip { padding: 6px 10px; background: #fff; border: 1px solid #e0e8f3; color: #76859a; border-radius: 9px; }
.tag-chip:hover, .tag-chip.active { background: #e2efff; border-color: #cce1ff; color: var(--kb-primary); }
.workspace-main { background: #f8faff; }
.document-browser, .editor-tabs { padding: 28px 38px; }
.browser-heading h2 { color: #263650; font-size: 23px; }
.browser-heading button, .editor-toolbar button, .panel-title button {
  padding: 9px 14px; border-radius: 10px; color: var(--kb-primary); background: #e4f0ff; font-weight: 500;
}
.browser-heading button:hover, .editor-toolbar button:hover, .panel-title button:hover { background: var(--kb-primary); color: #fff; }
.document-grid { gap: 16px; }
.document-card { min-height: 136px; padding: 18px; border: 1px solid #e5ebf4; border-radius: 14px; background: #fff; box-shadow: 0 3px 10px rgba(44,76,119,.04); }
.document-card:hover { border-color: #a9ccff; box-shadow: 0 9px 22px rgba(48,108,190,.1); transform: translateY(-1px); }
.doc-card-icon { background: #e7f1ff; color: var(--kb-primary); }
.document-card strong { color: #34435d; }
.document-card small, .document-card em { color: #98a4b5; }
.tab-bar { padding: 0 2px; border-bottom-color: #e1e8f1; }
.editor-tab { padding: 13px 16px; color: #8a98aa; border-radius: 10px 10px 0 0; }
.editor-tab:hover { background: #edf5ff; color: #4d75ad; }
.editor-tab.active { color: var(--kb-primary); background: #e3efff; border-bottom-color: var(--kb-primary); }
.editor-toolbar { padding: 17px 2px; }
.editor-toolbar input { color: #27364f; font-size: 21px; }
.editor-split { border: 1px solid #e2e9f2; border-radius: 14px; background: #fff; box-shadow: 0 3px 12px rgba(49,81,124,.04); }
.editor-split textarea { padding: 26px; background: #fbfcfe; color: #35445d; }
.preview { padding: 26px 30px; background: #fff; }
.context-block { border-bottom-color: #e7edf4; }
.panel-title { color: #253650; font-size: 14px; font-weight: 700; }
.property-row { color: #8998ab; border-bottom-color: #eef2f7; }
.property-row code { color: var(--kb-primary); }
.search-results { padding: 28px 38px; }
.search-result:hover { background: #edf5ff; }
.search-result strong { color: #2f405b; }
.search-result small, .search-result span { color: #8998aa; }
.command-palette { border: 1px solid #dae6f5; border-radius: 15px; background: #fff; box-shadow: 0 22px 56px rgba(45,80,130,.2); }
.command-palette input { background: #f7faff; color: #2c3b54; }
.command-palette button:hover { background: #eaf3ff; color: var(--kb-primary); }

[data-theme="dark"] .knowledge-workspace { --kb-bg: #111827; --kb-surface: #182235; --kb-input: #141e2e; --kb-text: #edf4ff; --kb-muted: #94a3b8; --kb-border: #2a3a52; --kb-primary-soft: rgba(22,119,255,.2); }
[data-theme="dark"] .workspace-toolbar, [data-theme="dark"] .context-sidebar { background: #182235; }
[data-theme="dark"] .vault-sidebar { background: #121c2c; border-right-color: #2a3a52; }
[data-theme="dark"] .workspace-main { background: #111827; }
[data-theme="dark"] .document-card, [data-theme="dark"] .preview { background: #182235; border-color: #2a3a52; }
[data-theme="dark"] .editor-split textarea { background: #121c2c; color: #dbeafe; }
</style>

<style scoped>
/* Workspace skin follows the main app's iOS-inspired surfaces and theme tokens. */
.knowledge-workspace {
  --kb-bg: var(--bg-main, #f4f7fb);
  --kb-surface: var(--bg-surface, #ffffff);
  --kb-input: var(--bg-input, #f7f9fc);
  --kb-text: var(--text-primary, #172033);
  --kb-muted: var(--text-secondary, #7b8496);
  --kb-border: rgba(31, 41, 55, .09);
  --kb-primary: var(--primary-color, #007aff);
  --kb-primary-soft: color-mix(in srgb, var(--kb-primary) 10%, transparent);
  background: var(--kb-bg);
  color: var(--kb-text);
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif;
}
.workspace-toolbar {
  height: 60px;
  padding: 0 22px;
  gap: 20px;
  background: color-mix(in srgb, var(--kb-surface) 92%, transparent);
  border-bottom: 1px solid var(--kb-border);
  backdrop-filter: blur(18px);
}
.workspace-brand { min-width: 156px; font-size: 15px; letter-spacing: .01em; }
.brand-mark {
  width: 28px; height: 28px; display: grid; place-items: center;
  color: var(--kb-primary); font-size: 12px; border-radius: 9px;
  background: var(--kb-primary-soft); box-shadow: 0 6px 16px rgba(0,122,255,.12);
}
.workspace-search { max-width: 680px; }
.workspace-search input {
  height: 36px; box-sizing: border-box; padding: 0 92px 0 38px;
  border: 1px solid var(--kb-border); border-radius: 11px;
  background: var(--kb-input); color: var(--kb-text); outline: none;
  transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
}
.workspace-search::before { content: '⌕'; position: absolute; left: 14px; top: 5px; z-index: 1; color: var(--kb-muted); font-size: 20px; }
.workspace-search input:focus { background: var(--kb-surface); border-color: color-mix(in srgb, var(--kb-primary) 55%, transparent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--kb-primary) 14%, transparent); }
.search-hint { top: 10px; color: var(--kb-muted); font-size: 11px; }
.workspace-actions { gap: 6px; }
.workspace-actions button {
  padding: 8px 12px; border-radius: 9px; color: var(--kb-muted); font-size: 13px;
  transition: background .18s ease, color .18s ease, transform .18s ease;
}
.workspace-actions button:first-child { background: var(--kb-primary); color: #fff; box-shadow: 0 7px 16px rgba(0,122,255,.2); }
.workspace-actions button:hover { color: var(--kb-text); background: var(--kb-primary-soft); transform: translateY(-1px); }
.workspace-actions button:first-child:hover { color: #fff; background: color-mix(in srgb, var(--kb-primary) 88%, #000); }
.workspace-body { grid-template-columns: 252px minmax(0, 1fr) 286px; }
.vault-sidebar, .context-sidebar {
  padding: 18px 14px; background: color-mix(in srgb, var(--kb-surface) 72%, transparent);
  border-color: var(--kb-border); scrollbar-width: thin;
}
.context-sidebar { background: color-mix(in srgb, var(--kb-surface) 52%, transparent); }
.sidebar-toolbar { margin: 0 5px 14px; color: var(--kb-muted); letter-spacing: .08em; font-size: 11px; }
.sidebar-toolbar button { width: 28px; height: 28px; border-radius: 8px; color: var(--kb-primary); background: var(--kb-primary-soft); font-size: 19px; line-height: 1; }
.sidebar-toolbar button:hover { background: var(--kb-primary); color: #fff; }
.sidebar-section { gap: 3px; }
.nav-item, .tree-row, .outline-row, .backlink-row, .revision-row {
  min-height: 34px; box-sizing: border-box; padding: 8px 10px; border-radius: 9px;
  color: var(--kb-muted); font-size: 13px; transition: background .16s ease, color .16s ease;
}
.nav-item:hover, .tree-row:hover, .outline-row:hover, .backlink-row:hover, .revision-row:hover,
.nav-item.active, .tree-row.active { background: var(--kb-primary-soft); color: var(--kb-primary); }
.nav-item small, .panel-title small { color: var(--kb-muted); font-weight: 500; }
.sidebar-heading { margin: 22px 8px 8px; color: var(--kb-muted); font-weight: 600; letter-spacing: .08em; }
.tree-row span { color: var(--kb-primary); opacity: .7; }
.tag-list { gap: 6px; padding: 0 4px; }
.tag-chip { background: var(--kb-input); color: var(--kb-muted); border: 1px solid var(--kb-border); transition: all .16s ease; }
.tag-chip:hover, .tag-chip.active { border-color: transparent; background: var(--kb-primary-soft); color: var(--kb-primary); }
.workspace-main { background: var(--kb-bg); }
.document-browser, .editor-tabs { padding: 28px 30px; }
.browser-heading { margin-bottom: 22px; }
.browser-heading h2 { font-size: 23px; font-weight: 700; letter-spacing: -.02em; }
.browser-heading button, .editor-toolbar button, .panel-title button {
  padding: 8px 12px; border-radius: 8px; color: var(--kb-primary); background: var(--kb-primary-soft); font-size: 12px;
}
.browser-heading button:hover, .editor-toolbar button:hover, .panel-title button:hover { background: var(--kb-primary); color: #fff; }
.document-grid { gap: 15px; }
.document-card {
  position: relative;
  min-height: 132px; padding: 17px; border: 1px solid var(--kb-border); border-radius: 13px;
  background: var(--kb-surface); box-shadow: 0 2px 8px rgba(15,23,42,.025);
  transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease;
}
.document-card:hover { transform: translateY(-2px); border-color: color-mix(in srgb, var(--kb-primary) 45%, transparent); box-shadow: 0 12px 26px rgba(15,23,42,.09); }
.doc-card-icon { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 8px; background: var(--kb-primary-soft); color: var(--kb-primary); }
.document-card strong { font-size: 14px; font-weight: 600; }
.document-card small, .document-card em { color: var(--kb-muted); }
.document-card i { color: #ffb000; }
.tab-bar { padding: 0 4px; border-bottom-color: var(--kb-border); }
.editor-tab { padding: 11px 14px; color: var(--kb-muted); border-radius: 8px 8px 0 0; }
.editor-tab:hover { color: var(--kb-text); background: var(--kb-primary-soft); }
.editor-tab.active { color: var(--kb-primary); border-bottom: 2px solid var(--kb-primary); background: var(--kb-primary-soft); }
.new-tab { width: 32px; height: 32px; border-radius: 8px; color: var(--kb-primary); }
.new-tab:hover { background: var(--kb-primary-soft); }
.editor-toolbar { padding: 14px 2px; gap: 12px; }
.editor-toolbar input { color: var(--kb-text); font-size: 19px; }
.save-state { color: var(--kb-muted); }
.editor-split { border-color: var(--kb-border); border-radius: 12px; box-shadow: 0 6px 20px rgba(15,23,42,.04); }
.editor-split textarea { padding: 22px; background: var(--kb-input); color: var(--kb-text); font-size: 14px; line-height: 1.8; }
.preview { padding: 24px 28px; background: var(--kb-surface); color: var(--kb-text); }
.context-block { padding: 4px 4px 18px; margin-bottom: 18px; border-color: var(--kb-border); }
.panel-title { color: var(--kb-text); font-size: 12px; letter-spacing: .03em; }
.property-row { color: var(--kb-muted); border-bottom: 1px solid color-mix(in srgb, var(--kb-border) 60%, transparent); }
.property-row code { color: var(--kb-primary); }
.empty-text, .context-empty { color: var(--kb-muted); }
.document-card-more { position: absolute; z-index: 3; top: 8px; right: 8px; }
.document-card-more-trigger {
  display: grid; width: 30px; height: 30px; place-items: center; padding: 0;
  border: 1px solid transparent; border-radius: 8px; background: transparent; color: var(--kb-muted); cursor: pointer;
}
.document-card-more-trigger:hover, .document-card-more-trigger[aria-expanded="true"] {
  border-color: var(--kb-border); background: var(--kb-primary-soft); color: var(--kb-primary);
}
.document-card-menu {
  position: fixed; z-index: 2000; width: 156px; padding: 5px;
  border: 1px solid var(--kb-border); border-radius: 10px; background: var(--kb-surface);
  box-shadow: 0 12px 28px rgba(15,23,42,.16);
}
.document-card-menu button {
  display: flex; align-items: center; gap: 8px; width: 100%; min-height: 36px; padding: 7px 9px;
  border: 0; border-radius: 7px; background: transparent; color: var(--kb-text); text-align: left; font-size: 13px; cursor: pointer;
}
.document-card-menu button:hover { background: var(--kb-primary-soft); color: var(--kb-primary); }
.document-card-menu button.danger { color: #d9363e; }
.document-card-menu button.danger:hover { background: #fff0f0; color: #c62828; }
[data-theme="dark"] .document-card-menu button.danger { color: #ff8b8b; }
[data-theme="dark"] .document-card-menu button.danger:hover { background: rgba(217,54,62,.15); color: #ffaaaa; }
.search-results { padding: 28px 30px; }
.search-result { padding: 16px 8px; border-bottom-color: var(--kb-border); border-radius: 9px; }
.search-result:hover { background: var(--kb-primary-soft); }
.search-result strong { color: var(--kb-text); }
.search-result small, .search-result span { color: var(--kb-muted); }
.command-overlay { background: rgba(15,23,42,.32); backdrop-filter: blur(5px); }
.command-palette { padding: 12px; background: var(--kb-surface); border: 1px solid var(--kb-border); border-radius: 16px; box-shadow: 0 24px 70px rgba(15,23,42,.24); }
.command-palette input { color: var(--kb-text); background: var(--kb-input); border-radius: 10px; }
.command-palette button { border-radius: 9px; color: var(--kb-muted); }
.command-palette button:hover { color: var(--kb-primary); background: var(--kb-primary-soft); }
.mobile-toolbar { background: var(--kb-surface); border-color: var(--kb-border); color: var(--kb-text); }

[data-theme="dark"] .knowledge-workspace {
  --kb-border: rgba(255,255,255,.1);
  --kb-primary-soft: color-mix(in srgb, var(--kb-primary) 18%, transparent);
}
[data-theme="dark"] .document-card, [data-theme="dark"] .preview { box-shadow: none; }
[data-theme="dark"] .editor-split textarea { background: color-mix(in srgb, var(--kb-input) 80%, #000); }

@media (max-width: 900px) {
  .workspace-body { grid-template-columns: 212px minmax(0, 1fr); }
  .document-browser, .editor-tabs { padding: 22px; }
}
@media (max-width: 640px) {
  .workspace-toolbar { height: 52px; padding: 0 12px; }
  .workspace-actions button { padding: 7px 9px; }
  .document-browser, .editor-tabs { padding: 14px; }
  .browser-heading h2 { font-size: 20px; }
  .editor-toolbar input { font-size: 17px; }
  .editor-split { border-radius: 10px; }
}
</style>

<style scoped>
/* Final shell overrides: match the host navigation rail exactly. */
.knowledge-workspace { --kb-bg:#f7f9fd; --kb-surface:#fff; --kb-input:#fff; --kb-text:#27364f; --kb-muted:#8997ab; --kb-border:#e4ebf5; --kb-primary:#1677ff; --kb-primary-soft:#e7f1ff; background:#f7f9fd; color:#27364f; }
.workspace-toolbar { height:64px; padding:0 24px; background:#fff; border-bottom:1px solid #e4ebf5; box-shadow:0 1px 0 rgba(27,75,135,.02); }
.workspace-brand { min-width:192px; color:#17243b; font-size:16px; }
.brand-mark { width:32px; height:32px; border-radius:11px; color:#1677ff; background:#e7f1ff; box-shadow:none; }
.workspace-search { max-width:845px; }
.workspace-search input { height:42px; padding:0 92px 0 42px; border:1px solid #e1e8f2; border-radius:13px; background:#fbfcfe; color:#27364f; }
.workspace-search::before { left:15px; top:50%; transform:translateY(-50%); line-height:1; color:#9aa7b8; font-size:21px; }
.workspace-search input:focus { background:#fff; border-color:#9fc5ff; box-shadow:0 0 0 3px rgba(22,119,255,.1); }
.search-hint { top:13px; color:#a0aabd; }
.workspace-actions { gap:6px; }
.workspace-actions button { border-radius:11px; padding:10px 15px; color:#8694a8; font-weight:500; }
.workspace-actions button:first-child { background:#1677ff; box-shadow:0 7px 14px rgba(22,119,255,.2); color:#fff; }
.workspace-actions button:hover { background:#e7f1ff; color:#1677ff; transform:none; }
.workspace-actions button:first-child:hover { background:#0d6ceb; color:#fff; }
.workspace-body { grid-template-columns:294px minmax(0,1fr) 280px; background:#f7f9fd; }
.vault-sidebar { padding:28px 17px; background:#f4f8fe; border-right:1px solid #e0e9f5; }
.context-sidebar { padding:30px 18px; background:#fff; border-left:1px solid #e3eaf3; }
.sidebar-toolbar { margin:0 7px 18px; color:#7f8da3; font-size:12px; letter-spacing:.06em; }
.sidebar-toolbar button { width:35px; height:35px; border-radius:10px; background:#e1efff; color:#1677ff; font-size:23px; }
.sidebar-toolbar button:hover { background:#1677ff; color:#fff; }
.sidebar-section { gap:6px; }
.nav-item,.tree-row,.outline-row,.backlink-row,.revision-row { min-height:42px; padding:10px 12px; border-radius:11px; color:#718198; font-size:14px; }
.nav-item:hover,.tree-row:hover,.outline-row:hover,.backlink-row:hover,.revision-row:hover,.nav-item.active,.tree-row.active { background:#e2efff; color:#1677ff; }
.nav-item.active { font-weight:600; }
.favorite-star { color:#f5b700 !important; }
.favorites-nav .favorite-star { margin-right:4px; }
.sidebar-heading { margin:25px 9px 9px; color:#8593a7; font-size:12px; font-weight:600; letter-spacing:.04em; }
.tree-row { min-height:38px; }
.tree-row span { color:#1677ff; }
.folder-item { position:relative; }
.folder-item .tree-row { display:flex; align-items:center; gap:7px; width:100%; padding-left:12px; }
.folder-chevron { width:14px; flex:0 0 14px; text-align:center; cursor:pointer; color:#1677ff !important; }
.folder-chevron.collapsed { opacity:.82; }
.folder-name { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.folder-more { margin-left:auto; flex:0 0 auto; padding:0 3px; border-radius:6px; color:#93a2b7 !important; font-size:13px; line-height:20px; letter-spacing:1px; opacity:.72; transition:opacity .16s ease, background .16s ease, color .16s ease; }
.folder-item:hover .folder-more, .folder-more:focus { opacity:1; }
.folder-more:hover { color:#1677ff !important; background:#d7eaff; }
.folder-menu { position:absolute; z-index:30; top:calc(100% - 3px); right:8px; min-width:142px; padding:6px; background:#fff; border:1px solid #dce7f4; border-radius:10px; box-shadow:0 12px 28px rgba(45,80,130,.16); }
.folder-menu button { display:block; width:100%; padding:9px 10px; border:0; border-radius:7px; background:transparent; color:#51627a; text-align:left; font-size:13px; cursor:pointer; }
.folder-menu button:hover { background:#eaf3ff; color:#1677ff; }
.folder-menu button.danger { color:#e05252; }
.folder-menu button.danger:hover { background:#fff0f0; color:#d9363e; }
.tag-list { gap:7px; }
.tag-chip { padding:6px 10px; background:#fff; border:1px solid #e0e8f3; color:#76859a; border-radius:9px; }
.tag-chip:hover,.tag-chip.active { background:#e2efff; border-color:#cce1ff; color:#1677ff; }
.workspace-main { background:#f8faff; }
.document-browser,.editor-tabs { padding:28px 38px; }
.browser-heading h2 { color:#263650; font-size:23px; }
.browser-heading button,.editor-toolbar button,.panel-title button { padding:9px 14px; border-radius:10px; color:#1677ff; background:#e4f0ff; font-weight:500; }
.browser-heading button:hover,.editor-toolbar button:hover,.panel-title button:hover { background:#1677ff; color:#fff; }
.document-card { min-height:136px; padding:18px; border:1px solid #e5ebf4; border-radius:14px; background:#fff; box-shadow:0 3px 10px rgba(44,76,119,.04); }
.document-card:hover { border-color:#a9ccff; box-shadow:0 9px 22px rgba(48,108,190,.1); transform:translateY(-1px); }
.doc-card-icon { background:#e7f1ff; color:#1677ff; }
.document-card strong { color:#34435d; }
.document-card small,.document-card em { color:#98a4b5; }
.document-card .favorite-star { position:absolute; right:13px; top:12px; font-size:17px; font-style:normal; }
.editor-tab { padding:13px 16px; color:#8a98aa; border-radius:10px 10px 0 0; }
.editor-tab:hover { background:#edf5ff; color:#4d75ad; }
.editor-tab.active { color:#1677ff; background:#e3efff; border-bottom-color:#1677ff; }
.editor-toolbar { padding:17px 2px; }
.editor-toolbar input { color:#27364f; font-size:21px; }
.favorite-button .favorite-star { margin-right:3px; font-size:15px; }
.favorite-button.is-favorite { color:#c99000 !important; background:#fff5d6 !important; }
.editor-tags { display:flex; align-items:center; flex-wrap:wrap; gap:7px; min-height:34px; padding:0 2px 14px; }
.editor-tags-label { color:#8b99aa; font-size:12px; font-weight:600; margin-right:2px; }
.editor-tag, .tag-suggestion, .editor-tag-add { border:0; border-radius:7px; padding:5px 8px; font-size:12px; cursor:pointer; }
.editor-tag { background:#e7f1ff; color:#1677ff; }
.editor-tag b { margin-left:3px; font-weight:500; opacity:.65; }
.editor-tag:hover { background:#d8eaff; }
.editor-tags input { width:150px; height:27px; padding:0 8px; border:1px solid #e1e8f2; border-radius:7px; outline:0; background:#fff; color:#27364f; font-size:12px; }
.editor-tags input:focus { border-color:#9fc5ff; box-shadow:0 0 0 2px rgba(22,119,255,.1); }
.editor-tag-add { background:#1677ff; color:#fff; }
.tag-suggestion { background:#f2f6fb; color:#7d8da2; }
.tag-suggestion:hover { background:#e7f1ff; color:#1677ff; }
.editor-split { border:1px solid #e2e9f2; border-radius:14px; background:#fff; box-shadow:0 3px 12px rgba(49,81,124,.04); }
.editor-split { z-index: auto; }
.editor-split textarea { padding:26px; background:#fbfcfe; color:#35445d; }
.preview { padding:26px 30px; background:#fff; }
.context-block { border-bottom-color:#e7edf4; }
.panel-title { color:#253650; font-size:14px; font-weight:700; }
.property-row { color:#8998ab; border-bottom-color:#eef2f7; }
.property-row code { color:#1677ff; }
.search-results { padding:28px 38px; }
.search-result:hover { background:#edf5ff; }
.command-palette { border:1px solid #dae6f5; border-radius:15px; background:#fff; box-shadow:0 22px 56px rgba(45,80,130,.2); }
.command-palette input { background:#f7faff; color:#2c3b54; }
.command-palette button:hover { background:#eaf3ff; color:#1677ff; }
[data-theme="dark"] .knowledge-workspace { --kb-bg:#111827; --kb-surface:#182235; --kb-input:#141e2e; --kb-text:#edf4ff; --kb-muted:#94a3b8; --kb-border:#2a3a52; --kb-primary-soft:rgba(22,119,255,.2); background:#111827; color:#edf4ff; }
[data-theme="dark"] .workspace-toolbar,[data-theme="dark"] .context-sidebar { background:#182235; }
[data-theme="dark"] .vault-sidebar { background:#121c2c; border-right-color:#2a3a52; }
[data-theme="dark"] .workspace-main { background:#111827; }
[data-theme="dark"] .document-card,[data-theme="dark"] .preview { background:#182235; border-color:#2a3a52; }
[data-theme="dark"] .editor-tags input { background:#141e2e; border-color:#2a3a52; color:#edf4ff; }
[data-theme="dark"] .tag-suggestion { background:#22324b; color:#b3c2d7; }
[data-theme="dark"] .folder-menu { background:#1b2940; border-color:#30445f; box-shadow:0 12px 28px rgba(0,0,0,.35); }
[data-theme="dark"] .folder-menu button { color:#dbeafe; }
</style>

<style scoped>
.workspace-body { grid-template-columns: 294px minmax(0, 1fr) 280px; transition: grid-template-columns .2s ease; }
.workspace-body { min-height: 0; }
.workspace-main { min-height: 0; }
.document-browser { min-height: 0; overflow: hidden; }
.document-browser .document-grid {
  min-height: 0;
  flex: 1 1 0;
  align-content: start;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}
.workspace-body.vault-collapsed { grid-template-columns: 0 minmax(0, 1fr) 280px; }
.workspace-body.context-collapsed { grid-template-columns: 294px minmax(0, 1fr) 0; }
.workspace-body.vault-collapsed.context-collapsed { grid-template-columns: 0 minmax(0, 1fr) 0; }
.vault-sidebar, .context-sidebar { min-width: 0; transition: opacity .16s ease, padding .2s ease, border-width .2s ease; }
.vault-sidebar.is-collapsed, .context-sidebar.is-collapsed { overflow: hidden; padding-left: 0; padding-right: 0; border-width: 0; opacity: 0; pointer-events: none; }
.sidebar-toolbar-actions { display: flex; align-items: center; gap: 5px; }
.sidebar-toolbar-actions button { display: inline-grid; place-items: center; }
.context-toolbar { display: flex; align-items: center; justify-content: space-between; min-height: 35px; margin: -8px 0 18px; color: var(--kb-muted, #8997ab); font-size: 12px; font-weight: 600; letter-spacing: .04em; }
.context-toolbar button { width: 30px; height: 30px; border: 0; border-radius: 8px; background: var(--kb-primary-soft, #e7f1ff); color: var(--kb-primary, #1677ff); cursor: pointer; font-size: 20px; line-height: 1; }
.context-toolbar button:hover { background: var(--kb-primary, #1677ff); color: #fff; }
.rail-toggle { min-width: 32px; padding: 8px 7px !important; color: var(--kb-muted, #8997ab) !important; }
.rail-toggle:hover { color: var(--kb-primary, #1677ff) !important; }
.document-count { margin-left: 8px; color: var(--kb-muted, #8997ab); font-size: 13px; font-weight: 400; letter-spacing: 0; white-space: nowrap; }
.vault-sidebar { background: #f7faff; border-right-color: #d8e3f1; }
.sidebar-toolbar { color: #5f7088; }
.sidebar-heading { color: #63748c; }
.nav-item,.tree-row { color: #43536b; }
.nav-item:hover,.tree-row:hover { background: #eaf3ff; color: #145fc2; }
.nav-item.active,.tree-row.active { background: #dcecff; color: #075fc9; }
.nav-item.active { font-weight: 650; }
.nav-item small { color: #5d6e84; font-size: 12px; font-weight: 600; }
.folder-name { flex: 1 1 auto; }
.folder-count { flex: 0 0 auto; margin: 0 0 0 auto !important; padding: 2px 7px; border-radius: 999px; background: #e7edf5; color: #53647b !important; font-size: 11px; font-weight: 600; line-height: 16px; }
.tree-row.active .folder-count { background: #fff; color: #145fc2 !important; }
.folder-more { margin-left: 2px; color: #718198 !important; opacity: 1; }
@media (max-width: 900px) {
  .workspace-body { grid-template-columns: 212px minmax(0, 1fr); }
  .workspace-body.vault-collapsed { grid-template-columns: 0 minmax(0, 1fr); }
  .workspace-body.context-collapsed, .workspace-body.vault-collapsed.context-collapsed { grid-template-columns: 212px minmax(0, 1fr); }
  .workspace-body, .workspace-body.vault-collapsed, .workspace-body.context-collapsed, .workspace-body.vault-collapsed.context-collapsed { display: flex; flex-direction: column; min-height: 0; }
  .workspace-main { flex: 1 1 0; min-height: 0; }
  .vault-sidebar:not(.mobile-open) { display: none; }
  .workspace-actions .rail-toggle { display: none; }
  .mobile-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 8px 14px; border-bottom: 1px solid var(--kb-border, #e4ebf5); }
  .mobile-toolbar button { width: 36px; height: 34px; border: 1px solid var(--kb-border, #e4ebf5); border-radius: 9px; background: var(--kb-surface, #fff); color: var(--kb-primary, #1677ff); cursor: pointer; }
  .context-sidebar.mobile-open { display: block; position: fixed; right: 0; top: 64px; bottom: 0; width: min(320px, 84vw); z-index: 20; background: var(--kb-surface, #fff); box-shadow: -10px 0 28px rgba(24, 55, 95, .16); opacity: 1; pointer-events: auto; }
  .vault-sidebar.mobile-open { display: block; position: fixed; left: 0; top: 64px; bottom: 0; width: min(300px, 82vw); z-index: 20; background: var(--kb-surface, #fff); box-shadow: 10px 0 28px rgba(24, 55, 95, .16); opacity: 1; pointer-events: auto; }
}
@media (max-width: 640px) {
  .workspace-body, .workspace-body.vault-collapsed, .workspace-body.context-collapsed, .workspace-body.vault-collapsed.context-collapsed { display: flex; flex-direction: column; min-height: 0; }
  .workspace-toolbar { display: none; }
  .vault-sidebar.mobile-open, .context-sidebar.mobile-open { top: 0; }
  .workspace-main { overflow-y: auto; -webkit-overflow-scrolling: touch; }
  .editor-tabs { flex: 1 0 auto; }
  .tab-bar { flex-shrink: 0; overflow-x: auto; overflow-y: hidden; }
  .workspace-actions .rail-toggle { display: none; }
  .vault-sidebar.is-collapsed, .context-sidebar.is-collapsed { display: none; }
  .vault-sidebar.mobile-open { display: block; width: min(86vw, 310px); padding: 18px 14px; border-right: 1px solid #e0e9f5; opacity: 1; pointer-events: auto; }
  .context-sidebar.mobile-open { display: block; width: min(88vw, 330px); padding: 20px 16px; border-left: 1px solid #e3eaf3; opacity: 1; pointer-events: auto; }
  .mobile-toolbar { position: sticky; top: 0; z-index: 4; min-height: 46px; }
  .mobile-toolbar button { width: 36px; height: 34px; border: 1px solid var(--kb-border, #e4ebf5); border-radius: 9px; background: var(--kb-surface, #fff); color: var(--kb-primary, #1677ff); cursor: pointer; }
  .mobile-toolbar span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; font-weight: 600; }
  .document-browser, .editor-tabs { padding: 12px; }
  .browser-heading { gap: 8px; align-items: flex-start; }
  .browser-heading h2 { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 19px; }
  .browser-heading button { flex: 0 0 auto; padding: 8px 10px; }
  .document-grid { grid-template-columns: minmax(0, 1fr); gap: 10px; width: 100%; }
  .document-card {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    min-height: 118px;
    padding: 14px 15px;
    gap: 6px;
    border-radius: 12px;
    overflow: hidden;
  }
  .document-card strong {
    display: -webkit-box;
    min-width: 0;
    overflow: hidden;
    line-height: 1.45;
    word-break: break-word;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
  .document-card small, .document-card em { display: block; min-width: 0; max-width: 100%; line-height: 1.45; }
  .document-card em { margin-top: 1px; }
  .editor-toolbar { flex-wrap: wrap; gap: 8px; padding: 11px 0; }
  .editor-toolbar input { flex: 1 1 100%; min-width: 0; font-size: 17px; }
  .folder-select { max-width: 145px; flex: 1 1 120px; }
  .editor-title-fields { display: contents; }
  .editor-toolbar-actions { width: auto; flex: 1 1 auto; justify-content: flex-end; }
  .markdown-editor-host { min-height: 48vh; }
  .preview { display: none; }
}
@media (max-width: 390px) {
  .workspace-actions button { padding-left: 6px; padding-right: 6px; }
  .document-grid { grid-template-columns: 1fr; }
  .workspace-search input { padding-left: 30px; }
  .workspace-search::before { left: 10px; }
}
</style>
